<template>
  <div class="advanced-modal" :class="{ isMobile }">
    <!-- 顶部标题栏 -->
    <div class="modal-header">
      <div class="header-left" @click="handleBack">
        <svg class="back-icon" viewBox="0 0 1024 1024">
          <path d="M724 96 L316 512 L724 928" fill="none" stroke="currentColor" stroke-width="100" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="modal-title">{{ pageTitle }}</span>
      </div>
      <span class="crown-badge" title="累计闯关成功次数">👑 × {{ gameWinCount }}</span>
      <!-- ★ 草稿纸入口（仅电脑端） -->
      <el-tooltip v-if="!isMobile" class="item" effect="dark" placement="top" content="草稿纸">
        <i class="el-icon-edit-outline rough-paper-entry" @click="openGlobalRoughPaper"></i>
      </el-tooltip>

      <!-- 桌面端：平铺按钮 -->
      <div class="header-actions">
        <button class="import-material-btn" @click="showSharedDialog = true">共享知识</button>
        <!-- ★ 学习资料导入按钮 -->
        <button class="import-material-btn" @click="showImportDialog = true">
          <svg viewBox="0 0 1024 1024" class="btn-icon">...</svg>
          学习资料导入
          <span v-if="importGraphReady" class="ready-dot"></span>
        </button>
        <button class="import-material-btn io-btn" @click="handleExportZip" :disabled="exporting">
          {{ exporting ? '导出中…' : '导出学习记录' }}
        </button>
        <button class="import-material-btn io-btn" @click="$refs.importFile.click()">
          导入学习记录
        </button>
        <input type="file" accept=".zip" ref="importFile" style="display:none" @change="handleImportZip" />
      </div>
      
      <!-- ★ 移动端：收进「更多」菜单 -->
      <div class="header-more" v-if="isMobile">
        <button class="import-material-btn more-btn" @click.stop="moreMenuOpen = !moreMenuOpen">
          ⋯
          <span v-if="importGraphReady" class="ready-dot"></span>
        </button>
        <transition name="fade">
          <div class="more-menu" v-if="moreMenuOpen" @click="moreMenuOpen = false">
            <div class="menu-item" @click="showSharedDialog = true">共享知识</div>
            <div class="menu-item" @click="showImportDialog = true">
              学习资料导入
              <span v-if="importGraphReady" class="ready-dot inline"></span>
            </div>
            <div class="menu-item" @click="handleExportZip" :class="{ disabled: exporting }">
              {{ exporting ? '导出中…' : '导出学习记录' }}
            </div>
            <div class="menu-item" @click="$refs.importFile.click()">导入学习记录</div>
          </div>
        </transition>
      </div>
    </div>
    <SharedKnowledge
      v-if="showSharedDialog"
      @close="showSharedDialog = false"
      @use-graph="handleUseSharedGraph"
    />
    <!-- ★ 资料导入弹窗 -->
    <ImportMaterialDialog
      :visible.sync="showImportDialog"
      :level="currentLevel"
      @use-graph="handleUseImportedGraph" 
      @graph-ready="importGraphReady = true"     
      @graph-consumed="importGraphReady = false" 
    />

    <!-- 主体：三栏 -->
    <div class="modal-body" :class="{ isMobile }" ref="modalBody">
      <!-- 左：图谱 -->
      <div class="graph-section" ref="graphSection"
           :style="{ flexBasis: collapseLeft ? '32px' : graphWidth + '%' }">
        <div class="section-title">
          <template v-if="!collapseLeft">
            <span class="title-text">{{ graphName }} | 知识地图</span>
            <!-- ★ 新增：重置视图图标 -->
            <span class="layout-icon" title="重置视图" @click="resetGraphView">
              <svg viewBox="0 0 1024 1024">
                <path d="M512 192 A320 320 0 1 0 832 512" fill="none" stroke="currentColor"
                      stroke-width="72" stroke-linecap="round"/>
                <polygon points="512,64 736,192 512,320" fill="currentColor"/>
              </svg>
            </span>
            <span class="layout-icon" :class="{ active: isTreeMode }" title="树形布局" @click="setLayoutMode('tree')">
              <svg viewBox="0 0 1024 1024">
                <line x1="200" y1="280" x2="824" y2="280" stroke="currentColor" stroke-width="76" stroke-linecap="round"/>
                <line x1="200" y1="512" x2="640" y2="512" stroke="currentColor" stroke-width="76" stroke-linecap="round"/>
                <line x1="200" y1="744" x2="456" y2="744" stroke="currentColor" stroke-width="76" stroke-linecap="round"/>
              </svg>
            </span>
            <span class="layout-icon" :class="{ active: !isTreeMode }" title="环形布局" @click="setLayoutMode('circle')">
              <svg viewBox="0 0 1024 1024">
                <circle cx="512" cy="512" r="300" fill="none" stroke="currentColor" stroke-width="46" stroke-dasharray="60 120" stroke-linecap="round"/>
                <circle cx="512" cy="212" r="60" fill="currentColor"/>
                <circle cx="790" cy="565" r="60" fill="currentColor"/>
                <circle cx="568" cy="790" r="60" fill="currentColor"/>
                <circle cx="268" cy="620" r="60" fill="currentColor"/>
                <circle cx="512" cy="512" r="46" fill="currentColor"/>
              </svg>
            </span>
          </template>
          <span v-if="!isMobile" class="collapse-icon" @click="toggleLeft"
                :title="collapseLeft ? '展开侧栏' : '收起侧栏'">
            <svg viewBox="0 0 1024 1024" :class="{ collapsed: collapseLeft }">
              <rect x="200" y="200" width="624" height="624" rx="40" fill="none" stroke="#666" stroke-width="70"/>
              <line x1="624" y1="200" x2="624" y2="824" stroke="#666" stroke-width="70"/>
            </svg>
          </span>
        </div>
        <div ref="modalGraph" class="modal-graph" v-show="!collapseLeft"></div>
      </div>

      <div v-if="!isMobile" class="divider" @mousedown="startDrag('left', $event)"></div>

      <!-- 中：内容 -->
      <div class="content-section" ref="contentSection">
        <div class="section-title">
          <span class="title-text">{{ graphName }} | 学习详情</span>
        </div>
        <!-- ★ 学习进度摘要 + 诊断进行中状态 + 诊断历史入口 -->
        <div class="progress-summary">
          <span v-if="graphStats.total">
            知识点学习进度：{{ graphStats.learned + graphStats.mastered }}/{{ graphStats.total }}
            <template v-if="graphStats.mastered">（已掌握 {{ graphStats.mastered }}）</template>
          </span>
          <span v-else class="muted">尚未开始学习</span>
          <span v-if="diagStep > 0 && isDiagnosing && !isGameMode" class="diag-step">
            诊断进行中：第 {{ diagStep }}/{{ DIAG_TOTAL }} 题已提交
          </span>
          <span class="diag-entry" @click="showDiagnosisHistory = !showDiagnosisHistory">
            诊断记录 ▾
          </span>
          <!-- ★ 闯关记录入口（与诊断记录并列） -->
          <span class="diag-entry" @click="showGameHistory = !showGameHistory">
            闯关记录 ▾
          </span>
        </div>

        <!-- ★ 诊断记录分组 -->
        <div class="record-block" v-if="showDiagnosisHistory">
            <div class="record-head">
                <span class="record-title">诊断记录</span>
                <span v-if="graphStats.diagnosisRecords.length" class="diag-clear" @click="handleClearAllRecords">全部删除诊断记录？</span>
            </div>
            <div class="diagnosis-history">
                <div v-for="r in graphStats.diagnosisRecords" :key="r.id" class="diag-item"
                    :class="{ clickable: r.status === 'done' }"
                    @click="r.status === 'done' && viewDiagnosisReport(r.id)">
                    <span class="diag-time">{{ formatTime(r.time) }}</span>
                    <span class="diag-path">{{ r.path }}</span>
                    <span class="diag-meta">{{ r.questions }}题 · {{ formatDuration(r.duration) }}</span>
                    <span :class="r.status === 'done' ? 'ok' : 'warn'">
                        {{ r.status === 'done' ? '完成' : '中断' }}
                    </span>
                    <span v-if="r.status === 'done'" class="diag-view">查看报告 ›</span>
                    <span class="diag-del" title="删除该记录" @click.stop="handleDeleteRecord(r.id)">🗑</span>
                </div>
                <div v-if="!graphStats.diagnosisRecords.length" class="diag-item">暂无诊断记录</div>
            </div>
        </div>

        <!-- ★ 闯关记录分组 -->
        <div class="record-block" v-if="showGameHistory">
            <div class="record-head">
                <span class="record-title">闯关记录</span>
                <span v-if="gameRecords.length" class="diag-clear" @click="handleClearGameRecords">全部删除闯关记录？</span>
            </div>
            <div class="diagnosis-history">
                <div v-for="r in gameRecords" :key="r.id" class="diag-item"
                    :class="{ clickable: r.status === 'done' }"
                    @click="r.status === 'done' && viewGameReport(r.id)">
                    <span class="diag-time">{{ formatTime(r.time) }}</span>
                    <span class="diag-path">{{ r.path }}</span>
                    <span class="diag-meta">{{ r.questions }}轮 · {{ formatDuration(r.duration) }}</span>
                    <span :class="r.result === 'success' ? 'ok' : 'warn'">
                        {{ r.result === 'success' ? '👑 成功' : r.result === 'aborted' ? '中断' : '失败' }}
                    </span>
                    <span v-if="r.status === 'done'" class="diag-view">查看报告 ›</span>
                    <span class="diag-del" title="删除该记录" @click.stop="handleDeleteGameRecord(r.id)">🗑</span>
                </div>
                <div v-if="!gameRecords.length" class="diag-item">暂无闯关记录</div>
            </div>
        </div>

        <!-- ★ 生成中：终止请求 -->
        <div class="stop-generating" v-if="loading || isDiagnosing || isNormalChatActive" @click="stopGenerating" title="终止生成">
          <svg viewBox="0 0 1024 1024">
            <rect x="288" y="288" width="448" height="448" rx="48" fill="currentColor"/>
          </svg>
          <span>{{ loading ? '终止生成'
                : isGameMode ? '终止闯关'
                : isDiagnosing ? '终止诊断'
                : '终止对话' }}</span>
        </div>

        <!-- ★ 查看报告时，若有流仍在进行/已结束，显示返回横幅 -->
          <div class="generating-banner" v-if="viewingReport && (loading || isDiagnosing || flowDone || nodeStreamActive)" @click="backToGenerating">
          <span class="dot" :class="{ done: pendingDone }"></span>
          <span v-if="nodeStreamActive && !pendingDone">节点内容生成中，点击返回实时页面</span>
          <span v-else-if="isDiagnosing && !pendingDone">诊断正在生成中，点击返回实时页面</span>
          <span v-else>生成已完成，点击查看结果</span>
          <span class="arrow">›</span>
        </div>

        <div class="content-box" v-html="contentRendered"></div>

        <!-- 底部：对话模式 → 聊天输入栏；学习模式 → 诊断/闯关按钮 -->
        <div class="content-footer chat-footer" v-if="chatWithMode">
          <!-- @ 图谱候选弹层（向上弹，锚定在输入栏上方） -->
          <div class="cwt-mention-pop" v-if="mentionVisible">
            <div class="mention-item"
                v-for="g in mentionCandidates"
                :key="g.kgid + g.level"
                @mousedown.prevent="pickMention(g)">
              {{ g.name }}（{{ levelLabel(g.level) }}）
            </div>
            <div v-if="!mentionCandidates.length" class="mention-item muted">无可选图谱</div>
          </div>
          <!-- 已选 @kgid 标签（可删除） -->
          <div class="cwt-kgid-area">
            <span class="cwt-tag" v-for="id in chatWithKgids" :key="id">
              @{{ id }}
              <i class="tag-del" @click="removeChatWithKgid(id)">✕</i>
            </span>
            <span v-if="!chatWithKgids.length" class="cwt-placeholder">输入@选择要对话的图谱（最多 3 个）</span>
          </div>
          <!-- 输入框 + 发送 -->
          <div class="cwt-input-row">
            <textarea
              ref="chatWithInput"
              v-model="chatWithText"
              placeholder="输入问题；输入@可引用其他图谱…"
              rows="1"
              @input="onChatWithInput"
              @keydown.enter.exact.prevent="sendChatWith"
              @blur="mentionVisible = false"
            ></textarea>
            <button class="cwt-send"
                    :disabled="!chatWithText.trim() || !chatWithKgids.length || chatWithLoading"
                    @click="sendChatWith">
              {{ chatWithLoading ? '…' : '发送' }}
            </button>
          </div>
          <!-- 退出对话模式，回到学习 -->
          <span class="cwt-exit" @click="exitChatWithMode" title="退出对话模式">退出对话</span>
        </div>
        <div class="content-footer" v-else>
          <button class="diagnosis-btn" @click="handleDiagnosisTest">诊断测试</button>
          <button class="diagnosis-btn game-btn" @click="handleGameTest">闯关游戏</button>
          <button class="diagnosis-btn chat-btn" @click="openChatWithDialog">与我对话</button>
        </div>


      </div>

      <div v-if="!isMobile" class="divider" @mousedown="startDrag('right', $event)"></div>

      <!-- 右：我的图谱（带进度条 + 学段标签） -->
      <div class="card-section" ref="cardSection"
           :style="{ flexBasis: collapseRight ? '32px' : cardWidth + '%' }">
        <div class="section-title">
          <span class="title-text" v-show="!collapseRight">我的图谱</span>
            <!-- ★ 信息图标：桌面悬停显示 title，移动端点击弹 message -->
          <span
            class="info-icon"
            v-show="!collapseRight"
            title="与「我的收藏本」里的「图谱收藏」保持一致"
            @click="$message.info('与「我的收藏本」里的「图谱收藏」保持一致')"
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
              <circle cx="12" cy="7.5" r="1.4" fill="currentColor"/>
              <rect x="10.9" y="10.5" width="2.2" height="7" rx="1.1" fill="currentColor"/>
            </svg>
          </span>

          <span v-if="!isMobile" class="collapse-icon" @click="toggleRight"
                :title="collapseRight ? '展开侧栏' : '收起侧栏'">
            <svg viewBox="0 0 1024 1024" :class="{ collapsed: collapseRight }">
              <rect x="200" y="200" width="624" height="624" rx="40" fill="none" stroke="#666" stroke-width="70"/>
              <line x1="400" y1="200" x2="400" y2="824" stroke="#666" stroke-width="70"/>
            </svg>
          </span>
        </div>
        <div class="card-list" v-show="!collapseRight" :class="{ scrollable: isMobile && userGraphs.length > 3 }">
          <div class="graph-card" v-for="g in sortedUserGraphs" :key="g.kgid + '-' + g.level"
               :class="{ active: currentKgId === g.kgid && currentLevel === g.level }"
               @click="loadGraph(g)">
            <div class="card-name">
              {{ g.name }}
              <span v-if="isPinned(g.kgid)" class="pin-tag">置顶</span>
              <span class="level-tag" :class="g.level">{{ levelLabel(g.level) }}</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: getCardPercent(g) + '%' }"></div>
            </div>
            <div class="card-progress-text">
              已学 {{ getCardLearned(g) }}<template v-if="getCardTotal(g)">/{{ getCardTotal(g) }}</template>
              · 诊断 {{ getCardDiagCount(g) }} 次
              <span v-if="getCardActiveStep(g) > 0" class="diag-step">
                · 诊断中 {{ getCardActiveStep(g) }}/{{ DIAG_TOTAL }}
              </span>
            </div>
            <!-- ★ 新增：三点图标 + 弹出菜单 -->
            <div class="more-actions" @click.stop="activeMenuKgId = activeMenuKgId === g.kgid ? null : g.kgid">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <circle cx="12" cy="5" r="1.5"/>
                <circle cx="12" cy="12" r="1.5"/>
                <circle cx="12" cy="19" r="1.5"/>
              </svg>
              
              <!-- 弹出菜单 -->
              <transition name="fade">
                <div class="dropdown-menu" v-if="activeMenuKgId === g.kgid" @click.stop>
                  <div class="menu-item" @click="togglePin(g.kgid)">
                    {{ isPinned(g.kgid) ? '取消置顶' : '置顶' }}
                  </div>
                  <div class="menu-item danger" @click="handleDeleteGraph(g)">
                    删除
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div v-if="!userGraphs.length" class="card-empty">在对应年级的智能体里生成知识图谱或收藏知识图谱<br> 该图谱将自动带入<br> 可以在「初中图谱导航」或「高中图谱导航」里找到感兴趣的图谱</div>
        </div>
      </div>
    </div>
    <!-- ★ 全局草稿纸弹窗（与 chatList.vue 一致） -->
    <el-dialog v-show="globalRoughPaperVisible" v-dialogDrag
      :width="globalRoughPaperFullscreen ? '100%' : '50%'"
      :top="globalRoughPaperFullscreen ? '0' : '21vh'"
      class="globalRoughPaperDialog"
      :class="{ 'is-fullscreen': globalRoughPaperFullscreen }"
      :close-on-click-modal="false"
      :visible.sync="globalRoughPaperVisible"
      :append-to-body="true" :modal="false"
      ref="globalRoughPaperDialogRef"
      @closed="handleDialogClosed">
      <span slot="title" class="rough-paper-title">
        <i class="el-icon-edit-outline draft-icon"></i>
        <span>草稿纸</span>
      </span>
      <draw-panel class="draw-panel"
        :fullscreen.sync="globalRoughPaperFullscreen"
        :storage-key="'global_drawpad'"
        @close="closeGlobalRoughPaper" />
      <div slot="footer" class="dialog-footer base-button">
        <button @click="globalRoughPaperVisible = false" style="margin-right: 10px">取 消</button>
      </div>
    </el-dialog>
  </div>
</template>


<script>
import ImportMaterialDialog from '@/components/learning/ImportMaterialDialog.vue';
import graphRender from "@/mixin/graphRender";
import md from "@/utils/markdown/md";
import request, { getBaseURL } from "@/utils/request";
import { handleMathData as rawHandleMathData } from '@/api/fetch/mathService';
import { createAutoMsg } from '@/utils/chat/autoMsg';
import { nanoid } from 'nanoid';
import JSZip from 'jszip';
import { showGraphNames, showGraph, removeGraph, showSharedKgs } from "@/api/chat/knowledgeGraph";
import { handleChatParams } from '@/utils/fetch/StreamProcessor';
import { fetchLocalStream } from '@/utils/learning/localStream';
import * as chatApi from "@/api/chat/sendReceive";
import { getCachedGraph, setCachedGraph, removeCachedGraph } from '@/utils/learning/kgCache';

// ★ 学习进度/诊断记录存储（报告全文已存 record.report，本地缓存即可找回）
import {
  markNodeLearned,
  markNodeMastered,
  getGraphStats,
  setGraphNodeTotal,
  startDiagSession,
  getDiagSession,
  recordDiagAnswer,
  finishDiagSession,
  abortDiagSession,
  getNodeStatus,
  getDiagnosisRecord,
  deleteDiagnosisRecord,
  clearDiagnosisRecords,
  exportAllProgress,
  importAllProgress,
  removeGraphProgress,   
  startGameSession, getGameSession, recordGameAnswer, finishGameSession,
  abortGameSession, getGameRecords, markNodeGame, 
  getGameWinCount, incrGameWinCount, deleteGameRecord, clearGameRecords,

} from '@/utils/learning/progressStore';

import SharedKnowledge from '@/components/learning/SharedKnowledge.vue';
import { startNodeFlow, startDiagFlow, getFlow, getFlowOrSnapshot, getFlowKey,
         updateFlow, stopFlow, subscribe, detachFlowHooks, clearUnseenFlows,
         isFlowUnseen, BUSY_TEXT_RE } from '@/utils/learning/backgroundFlow';
import drawPanel from '@/components/draw/index.vue';
const TREE_MODE = 'center';
// ★ 与我对话：最多可 @ 的图谱数量（后期调整只改这一处）
const MAX_CHAT_MENTIONS = 3;

// ★★★ level → 智能体 key 映射：
//     h → exercises（高中） m → mexercises（初中） p → pexercises（小学）
const LEVEL_ROBOT_KEY = {
  h: 'exercises',
  m: 'mexercises',
  p: 'pexercises',
};

// ★★★ 反向映射：入口智能体 → level（初始进入时由 store.robotId 反推，与 chatAside 体系同源）
const ROBOT_LEVEL = {
  exercises: 'h',
  mexercises: 'm',
  pexercises: 'p',
};

export default {
  name: "AdvancedLearning",
  components: { ImportMaterialDialog, SharedKnowledge, drawPanel},   // ★ 注册
  mixins: [graphRender],
  props: {
    kgId: String,
    paintData: Object,
    // ★ 初始 level（父组件打开弹窗时传入，如 'h'；缺省时由 store.robotId 反推）
    level: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isNormalChatActive: false,
      handleCopyCode: () => {},
      globalRoughPaperVisible: false,
      globalRoughPaperFullscreen: false,
      gameRecords: [],   // 当前图谱闯关历史
      showGameHistory: false, // ★ 补上：闯关历史区显隐（Vue 2 必须声明才响应式）
      entryLevel: this.level || this.paintData?.level || ROBOT_LEVEL[this.$store.state.robotId] || '',
      showSharedDialog: false,
      pinnedKgIds: [],        // 置顶的 kgid 列表
      activeMenuKgId: null,   // 当前打开菜单的卡片 kgid（控制菜单显隐）
      moreMenuOpen: false,
      importGraphReady: false, // ★ 导入图谱已生成待使用（红点）
      exporting: false,
      showImportDialog: false,
      isMobile: window.innerWidth < 1023,
      userGraphs: [],
      currentKgId: this.kgId,
      // ★★★ 当前图谱学段。level 生命周期（唯一两个写入点，绝无第三处）：
      //     1) 初始：props.level → paintData.level → store.robotId 反推
      //        （从哪个学段的智能体进入，level 就是哪个学段，不依赖父组件必传）
      //     2) 切换图谱：loadGraph(g) 第一行锁定 g.level（与右侧卡片标签同源，已验证正确）
      //     其它任何地方只读不改，杜绝学段串线
      currentLevel:
        this.level
        || this.paintData?.level
        || ROBOT_LEVEL[this.$store.state.robotId]
        || '',
      currentPaintData: this.paintData,
      contentRendered: "<p>点击「知识地图节点」学习</p>",
      loading: false,
      nodePath: '',
      isDiagnosing: false,
      isGameMode: false,   // ★ 当前进行中的是闯关（true）还是诊断（false）
      // ★ 诊断状态机
      DIAG_TOTAL: 3,          // 诊断总题数（每题一轮对话，答完生成报告）
      diagStep: 0,            // 已提交题数（0 = 无进行中的诊断）
      gameStep: 0,   // ★ 闯关已提交轮数（0 = 无进行中的闯关）
      gameWinCount: getGameWinCount(),
      // ★ 学习进度展示
      graphStats: { learned: 0, mastered: 0, total: 0, percent: 0, diagnosisRecords: [] },
      graphNodeNames: [],     // 当前图谱全部节点名
      showDiagnosisHistory: false,
      // ★ 查看报告模式 + 双流缓冲
      viewingReport: false,     // 是否处于"查看报告"模式
      pendingStreamHtml: '',    // 查看报告期间，流式内容的缓冲区
      pendingDone: false,       // 查看期间流是否已结束
      nodeStreamActive: false,  // 节点内容流是否进行中（响应式，供横幅条件用）
      // ★★★ 弹窗自管会话：每个智能体 key 一个 sessionId
      //     直调 robot.startNewSession()（公用主窗口底层接口），
      //     结果只存这里（组件内存，关弹窗丢弃），
      //     不写入 store 长期状态、不出现在主窗口历史列表
      sessionMap: {},
      // 三栏布局
      graphWidth: 35,
      cardWidth: 20,
      lastGraphWidth: 35,
      lastCardWidth: 20,
      isDragging: false,
      dragType: null,
      startX: 0,
      startGraphWidth: 0,
      startCardWidth: 0,
      collapseLeft: false,
      collapseRight: false,
      // ★ 与我对话
      chatWithMode: false,        // 是否处于"与我对话"模式（底部按钮区切换为输入栏）
      chatWithKgids: [],          // 已选图谱 id（不带 @）
      chatWithText: '',           // 输入框内容
      mentionVisible: false,      // @ 图谱候选弹层显隐
      chatWithLoading: false,     // 发送中
      chatWithKgLevels: {},     // ★ 新增：kgid → 该图谱自带学段，如 { kg_abc123: 'h', kg_def456: 'p' }
    };
  },
  computed: {
    // @ 弹层候选：所有"我的图谱"，排除已选
    mentionCandidates() {
      // ★ 满 2 个后不再提供候选
      if (this.chatWithKgids.length >= MAX_CHAT_MENTIONS) return [];
      return this.userGraphs.filter(g => !this.chatWithKgids.includes(g.kgid));
    },
    flowDone() {
      const t = getFlow(this._flowKey);
      return !!t && (t.done || t.failed);
    },
    sortedUserGraphs() {
      // 置顶的排最前，保持原相对顺序
      const pinned = this.userGraphs.filter(g => this.isPinned(g.kgid));
      const others = this.userGraphs.filter(g => !this.isPinned(g.kgid));

      // ★ 非置顶：带入学段权重 0 优先，其余按 h→m→p
      // entryLevel 无效 → 不排序，保持后端返回的原顺序
      if (LEVEL_ROBOT_KEY[this.entryLevel]) {
        const order = { h: 1, m: 2, p: 3 };
        order[this.entryLevel] = 0; // 有效入口学段 → 提到最前
        others.sort((a, b) => (order[a.level] ?? 99) - (order[b.level] ?? 99));
      }

      return [...pinned, ...others];
    },
    graphName() {
      return this.currentPaintData?.name || this.currentPaintData?.title || '';
    },
    pageTitle() {
      const levelNames = {
        h: '高中智能体',
        m: '初中智能体',
        p: '小学智能体',
      };
      const robotName = levelNames[this.currentLevel];
      if (robotName && !this.isMobile) {
        return `带着地图学习 · ${robotName}`;
      }
      return '带着地图学习';
    },
    isTreeMode() {
      return this.layoutMode === TREE_MODE;
    },
    graphListOverflow() {
    return this.userGraphs.length > 4;
    },
  },
  created() {
    // ★ 防重复提交记录（非响应式）
    this._lastDiagRequest = { payload: '', time: 0 };
    // ★ 挂起的诊断收尾（查看报告期间流完成时暂存，返回时补做）
    this._pendingFlowDone = null;
    this._ownTextPayloads = new Set();
  },

  mounted() {
      this.copyCode();
      // ★ 熄灭入口红点的持久状态（入口本地已即时置 false，此处负责 seen 落盘）
      clearUnseenFlows();
      // ★ 订阅 service：任何流变化 → 刷新视图
      this._unsubFlow = subscribe(() => this.syncFlowView());
      try {
          this.pinnedKgIds = JSON.parse(localStorage.getItem('kg_pinned_list') || '[]');
      } catch (e) {
          this.pinnedKgIds = [];
      }
      this._onDocClick = () => { this.moreMenuOpen = false; };
      document.addEventListener('click', this._onDocClick);
      document.body.classList.add('advanced-learning-open');
      if (this.currentPaintData) {
          this.initGraphSvg();
          this.$nextTick(() => this.collectNodeNames());
      }
      // ★★★ 点击外部关闭图谱卡片菜单 ★★★
      this._onCardMenuDocClick = () => {
          if (this.activeMenuKgId !== null) {
              this.activeMenuKgId = null;
          }
      };
      document.addEventListener('click', this._onCardMenuDocClick);

      // ★ 内存流恢复（页面未刷新、外部传入图谱的正常场景）
      this.restoreBackgroundFlow();

      // ★★★ 拉取图谱列表后，扫描并自动加载"最新有流/有快照"的图谱 ★★★
      // 需求：退出到主窗口再进入 → 保持最新状态；只有外部明确指定图谱（从图谱里进入）才不抢
      this.fetchUserGraphs().then(() => {
          if (this.currentKgId && this.currentPaintData) return;

          const candidates = this.userGraphs
              .map(g => ({ g, t: getFlowOrSnapshot(getFlowKey(g.kgid, g.level)) }))
              .filter(x => x.t)
              .sort((a, b) => (b.t.updatedAt || 0) - (a.t.updatedAt || 0));

          const active =
              candidates.find(x => !x.t.done && !x.t.failed && !x.t.snapshotOnly) ||      // 生成中
              candidates.find(x => (x.t.snapshotOnly || !!x.t.lastHtml) && isFlowUnseen(getFlowKey(x.g.kgid, x.g.level))) || // 完成未读（红点对应）
              candidates.find(x => !!x.t.lastHtml);                                        // 兜底

          if (active) {
              this.loadGraph(active.g);
          } else {
              const withSession = this.userGraphs.find(g =>
                  getDiagSession(g.kgid, g.level) || getGameSession(g.kgid, g.level));
              if (withSession) this.loadGraph(withSession);
          }
      });

      // ★ 恢复进行中的诊断/闯关会话（弹窗重开/页面刷新后）
      if (this.currentKgId) {
          const s = getDiagSession(this.currentKgId, this.currentLevel);
          this.diagStep = s ? s.step : 0;
          const gs = getGameSession(this.currentKgId, this.currentLevel);
          this.gameStep = gs ? gs.step : 0;
      }
      this.refreshGraphStats();
      console.log('[AdvancedLearning] 初始 level =', this.currentLevel,
          '(robotId =', this.$store.state.robotId + ')',
          '→ 智能体 key =', LEVEL_ROBOT_KEY[this.currentLevel] || '(无效!)');
      window.addEventListener('resize', this.handleResize);
      this.handleDiagTextClick();
      document.addEventListener('click', this.handleAlDownload);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleAlDownload);
    this._downloadCtx = null;
    document.removeEventListener('click', this.handleCopyCode);
    document.removeEventListener('click', this._onDocClick);
    document.body.classList.remove('advanced-learning-open');
    window.removeEventListener('resize', this.handleResize);
    if (this._onDiagTextClick) {
      // ★ 与注册时一致：捕获阶段（第三个参数 true）
      document.removeEventListener('click', this._onDiagTextClick, true);
      this._onDiagTextClick = null;
    }
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('click', this._onCardMenuDocClick);
    this._onCardMenuDocClick = null; // 释放引用
    if (this._doneTimer) { clearTimeout(this._doneTimer); }
    if (this._diagTimer) { clearTimeout(this._diagTimer); }
    clearTimeout(this._flowTimer);
    this._unsubFlow && this._unsubFlow();            // 退订
    this._destroyed = true;
    if (this._flowKey) detachFlowHooks(this._flowKey); // 断开 onDone 对组件的引用
  },
  methods: {
      // ════════ ★ 下载功能（参照 chatMain 的教师/学生下载，仅内存流期间可用） ════════
      // 追加下载按钮（流完成后由 syncFlowView 调用）
      appendDownloadButtons() {
        const t = getFlow(this._flowKey);
        if (!t || !t.done) return;
        const ai = t.aiContent;
        if (!ai || !ai.blockid) return;
        if (this.contentRendered.includes('al-dl-btn')) return; // 防重复

        const questionid = this.collectTeacherStudentIds(ai.parts || []);

        this._downloadCtx = {
          sessionId: ai.sessionId || '',
          blockid: ai.blockid,
          title: ai.parts?.[0]?.title || ai.title || '',
          questionid, // 二维数组（per part），与 chatMain 传参同构
          level: ai.params?.level || this.currentLevel, // ★ 图谱自带学段
        };

        this.contentRendered += `
          <div class="al-block-operation">
            <button class="bueaty-btn al-dl-btn" data-dl="teacher">教师下载</button>
            <button class="bueaty-btn al-dl-btn" data-dl="student">学生下载</button>
          </div>`;
      },

      // 收集题目 id —— 完全对齐 chatMain.handleTeacherDownload 的三层结构
      // aiContent.parts[].questions[type].questions[]（type = 单选/多选/填空/解答…）
      collectTeacherStudentIds(parts) {
        const questionid = [];
        if (!Array.isArray(parts)) return questionid;
        for (const part of parts) {
          const partIds = [];
          if (part && part.questions) {
            for (const type in part.questions) {
              const typeQuestions = part.questions[type];
              if (typeQuestions.questions && Array.isArray(typeQuestions.questions)) {
                for (const q of typeQuestions.questions) {
                  if (q.id) partIds.push(q.id);
                }
              }
            }
          }
          if (partIds.length) questionid.push(partIds); // 每个 part 一组
        }
        return questionid;
      },

      // 点击委托（v-html 内容必须事件委托到 document）
      async handleAlDownload(e) {
        const btn = e.target.closest('.al-dl-btn');
        if (!btn || !this._downloadCtx) return;
        if (btn.disabled) return;

        const ctx = this._downloadCtx;
        btn.disabled = true;
        const orig = btn.innerHTML;
        btn.innerHTML = '下载中…';

        try {
          const params = {
            sessionId: ctx.sessionId,
            blockid: ctx.blockid,
            title: ctx.title,
            questionid: ctx.questionid,
            level: ctx.level,
          };
          const res = btn.dataset.dl === 'teacher'
            ? await chatApi.teacherDownloadForGraph(params)
            : await chatApi.studentDownloadForGraph(params);
          await this.handleAlDownloadRes(res, ctx.title);
        } catch (error) {
          await this.handleAlDownloadError(error);
        } finally {
          btn.disabled = false;
          btn.innerHTML = orig;
        }
      },

      // 统一下载处理 —— 精简自 chatMain.handleDownload
      async handleAlDownloadRes(res, name) {
        if (!res) { this.$message.error('下载失败：无响应'); return; }
        if (res.status !== 200) { this.$message.error(`下载失败 (${res.status})`); return; }

        // 200 但 Content-Type 是 JSON → 后端返回的是错误信息而非文件
        const contentType = res.headers?.['content-type'] || '';
        if (contentType.includes('application/json')) {
          let errorMsg = '下载失败';
          try {
            let data = res.data;
            if (data instanceof Blob) data = JSON.parse(await data.text());
            errorMsg = data.message || data.error || JSON.stringify(data);
          } catch (e) { /* ignore */ }
          this.$message.error(errorMsg);
          return;
        }

        let filename = '';
        const cd = res.headers?.['content-disposition'] || '';
        const matches = cd.match(/filename="?([^"]+)"?/);
        if (matches) filename = matches[1];
        if (!filename) filename = name + '.docx';

        const blob = new Blob([res.data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('下载成功');
      },

      // 统一错误解析 —— 精简自 chatMain.handleDownloadError
      async handleAlDownloadError(error) {
        let errorMsg = '下载失败';
        try {
          if (error.response) {
            const { data, status, statusText } = error.response;
            if (data instanceof Blob) {
              const text = await data.text();
              try {
                const json = JSON.parse(text);
                errorMsg = json.message || json.msg || json.error || text;
              } catch { errorMsg = text || `HTTP ${status} ${statusText}`; }
            } else if (data && typeof data === 'object') {
              errorMsg = data.message || data.msg || data.error || JSON.stringify(data);
            } else if (typeof data === 'string') {
              errorMsg = data;
            } else {
              errorMsg = `HTTP ${status} ${statusText || ''}`.trim();
            }
          } else if (error.message) {
            errorMsg = error.message;
          }
        } catch (e) { errorMsg = '解析错误信息时发生异常'; }
        if (!errorMsg) errorMsg = '下载失败，请稍后重试';
        this.$message.error(errorMsg);
      },


      // ===== ★ 与我对话 =====
      // 进入对话模式：清空生成内容区，底部按钮组切换为输入栏，默认带入当前图谱
      openChatWithDialog() {
        // ★ 若当前图谱有流正在生成，先确认：终止旧流再进入对话模式
        const t = getFlow(this._flowKey);
        if (t && !t.done && !t.failed) {
          this.$confirm(
            '当前正在生成内容，进入对话模式将终止本轮生成，已生成部分将保留。是否继续？',
            '提示',
            { confirmButtonText: '终止并进入', cancelButtonText: '取消', type: 'warning' }
          ).then(() => {
            stopFlow(this._flowKey, '切换到对话模式');   // 终止并保留已生成快照
            this.enterChatWithMode();
          }).catch(() => {});
          return;
        }
        this.enterChatWithMode();
      },

      // 从 openChatWithDialog 中抽出的原逻辑
      enterChatWithMode() {
        this.chatWithMode = true;
        this.resetViewMode();
        this.isDiagnosing = false;
        this.isGameMode = false;
        this.nodeStreamActive = false;
        if (this.currentKgId && !this.chatWithKgids.includes(this.currentKgId)) {
          this.chatWithKgids = [this.currentKgId];
          // ★ 当前图谱自带学段：优先取列表里该图谱的 level，取不到再用 currentLevel
          const cur = this.userGraphs.find(x => x.kgid === this.currentKgId);
          this.chatWithKgLevels = { [this.currentKgId]: cur?.level || this.currentLevel };
        }
        this.setContent('<p>输入问题，与我对话</p>', { scrollTo: 'top' });
        this.$nextTick(() => this.$refs.chatWithInput?.focus());
      },

      // 退出对话模式：恢复诊断/闯关按钮组
      // 方案 A（推荐）：退出即清，每次进入都以当前图谱为基础
      exitChatWithMode() {
        this.chatWithMode = false;
        this.mentionVisible = false;
        this.chatWithText = '';
        this.chatWithKgids = [];
        this.chatWithKgLevels = {};
        this.setContent('<p>点击「知识地图节点」学习</p>', { scrollTo: 'top' });
      },

      // 删除某个已选 @kgid 标签
      removeChatWithKgid(id) {
        this.chatWithKgids = this.chatWithKgids.filter(k => k !== id);
        this.$delete(this.chatWithKgLevels, id);
      },

      // 输入监听：检测光标前刚输入的 @ → 弹出图谱候选
      onChatWithInput() {
        const el = this.$refs.chatWithInput;
        if (!el) return;
        const pos = el.selectionStart;
        const before = this.chatWithText.slice(0, pos);
        // ★ 原来：/@$/.test(before) —— 任意位置的 @ 都触发
        // ★ 现在：/^@$/.test(before) —— 必须是第一个字符，且前面不能有任何文字
        this.mentionVisible = /^@$/.test(before) && this.chatWithKgids.length < MAX_CHAT_MENTIONS;
      },

      // 选中某个图谱：kgid 加入标签区，移除输入框里的 @
      pickMention(g) {
        // 已选则视为取消，直接收起弹层
        if (this.chatWithKgids.includes(g.kgid)) {
          this.mentionVisible = false;
          return;
        }
        // ★ 限制最多 @ 两个图谱
        if (this.chatWithKgids.length >= MAX_CHAT_MENTIONS) {
          this.mentionVisible = false;
          this.$message.warning(`最多选择${MAX_CHAT_MENTIONS} 个图谱进行对话`);
          return;
        }
        this.chatWithKgids.push(g.kgid);
        // ★ 记录该图谱自带的学段（以列表数据为准，不使用 currentLevel）
        this.$set(this.chatWithKgLevels, g.kgid, g.level || this.currentLevel);
        const el = this.$refs.chatWithInput;
        if (el) {
          const pos = el.selectionStart;
          this.chatWithText = this.chatWithText.slice(0, pos - 1) + this.chatWithText.slice(pos);
          this.$nextTick(() => el.focus());
        }
        this.mentionVisible = false;
      },

      // 发送：文本 = @kgid1@kgid2…@用户问题，接口 /MathChat，本地流渲染
      async sendChatWith() {
      const q = this.chatWithText.trim();
      if (!q || !this.chatWithKgids.length || this.chatWithLoading) return;

      const sessionId = await this.getSessionIdForLevel(this.currentLevel, 'node');
      const robotId = this.getRobotIdByLevel(this.currentLevel);
      if (!sessionId || !robotId) {
        this.$message.warning('会话初始化失败，请稍后重试');
        return;
      }

      // ★ text 只放用户问题，不再拼接 @kgid
      // 图谱 id + 各自学段全部走结构化字段
      const kgLevels = {};
      this.chatWithKgids.forEach(id => {
        kgLevels[id] = this.chatWithKgLevels[id] || this.currentLevel;
      });

      this.chatWithLoading = true;
      this.chatWithText = '';
      this.mentionVisible = false;
      this.resetViewMode();
      this.isDiagnosing = false;
      this.isGameMode = false;
      this.isNormalChatActive = true;
      this.setContent('<p>⏳ 正在处理…</p>');

      const avatar = this.getRobotAvatarByLevel(this.currentLevel);
      const aiContent = createAutoMsg(avatar, this.$delete);
      aiContent.type = "graphCollect";
      aiContent.sessionId = sessionId;
      aiContent.robotId = robotId;
      aiContent.msg = "loading";
      aiContent.params = {
        text: q,                       // ★ 纯用户问题："这两个图谱有哪些共同知识点？"
        level: this.currentLevel,      // 主学段（会话归属）
        kgids: [...this.chatWithKgids], // ★ 图谱 id 列表（数组，保持选择顺序）
        kgLevels,                       // ★ kgid → 自带学段映射
        sessionId,
        robotId,
        fileList: [],
      };
        const controller = new AbortController();
        aiContent.controller = controller;
        if (!aiContent.id) aiContent.id = nanoid();

        const flowKey = getFlowKey(this.currentKgId, this.currentLevel);
        this._flowKey = flowKey;

        try {
          // ★ 请求接口 mathchat：本地直连，不写主窗口 store
          fetchLocalStream(
            `${getBaseURL()}/MathChat`,
            handleChatParams({ aiContent }),
            (json, content) => {
              if (!json.questions) json.questions = [];
              if (!json.base64) json.base64 = [];
              if (!json.plotly) json.plotly = {};
              try { rawHandleMathData(json, content); } catch (e) { console.warn(e); }
            }
          );
        } catch (error) {
          console.error('[AdvancedLearning] 对话请求失败:', error);
          this.setContent(`<p style="color:red;">请求失败：${error.message || '请稍后重试'}</p>`);
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          this.chatWithLoading = false;
          return;
        }

        // 复用诊断流：backgroundFlow 独立于组件，弹窗关闭也在后台跑
        startDiagFlow(flowKey, {
          aiContent, controller, payload: q,
          onDone: () => {
            this.isDiagnosing = false;
            this.isNormalChatActive = false;
            this.chatWithLoading = false;
          },
        });
        const task = getFlow(flowKey);
        if (task) task.isNormalChat = true;        // ★ 新增（与我对话恒为普通对话）
      },
      // ★ 借鉴 chatMain.vue：复制代码块
      copyCode() {
        this.initCopyCode();
        document.addEventListener('click', this.handleCopyCode);
      },
      initCopyCode() {
        this.handleCopyCode = async (e) => {
          if (!e.target.closest('.content-section')) return; // 只管弹窗内部
          if (e.target.classList.contains('copy-btn')) {
            const targetElement = e.target.parentElement.nextElementSibling;
            const code = decodeURIComponent(targetElement.innerText);
            try {
              await navigator.clipboard.writeText(code);
              this.$message.success('已复制!');
            } catch (err) {
              console.error('复制失败:', err);
              this.$message.warning('复制失败');
            }
          }
        };
      },
      /** 打开草稿纸：强制全屏，清除拖拽残留内联样式 */
      openGlobalRoughPaper() {
        // 先清残留，再置全屏，最后打开
        this.clearDialogInlineStyles();
        this.globalRoughPaperFullscreen = false; // 先复位，避免上次的中间态
        this.globalRoughPaperVisible = true;
        this.$nextTick(() => {
          this.clearDialogInlineStyles();
          // 打开渲染完成后再置全屏，确保 is-fullscreen 类和动态属性生效
          this.globalRoughPaperFullscreen = true;
          this.$nextTick(() => this.clearDialogInlineStyles());
        });
      },

      /** 全屏关闭按钮：直接关闭草稿纸弹窗（保留草稿内容） */
      closeGlobalRoughPaper() {
        this.globalRoughPaperVisible = false;
      },

      /** 弹窗完全关闭后：退出全屏状态 + 清内联样式 + 恢复默认 top */
      handleDialogClosed() {
        this.globalRoughPaperFullscreen = false;
        this.$nextTick(() => {
          this.clearDialogInlineStyles();
          const dialogRef = this.$refs.globalRoughPaperDialogRef;
          if (dialogRef && dialogRef.$el) {
            dialogRef.$el.style.top = '21vh';
          }
        });
      },

      /** 清除弹窗 DOM 上由 v-dialogDrag 产生的内联样式 */
      clearDialogInlineStyles() {
        const dialogRef = this.$refs.globalRoughPaperDialogRef;
        if (dialogRef && dialogRef.$el) {
          const el = dialogRef.$el;
          el.style.top = '';
          el.style.left = '';
          el.style.margin = '';
          el.style.transform = '';
        }
      },

      // ★ HTML 属性转义（描述/路径中可能含引号、尖括号、& 等）
      // 统一出口，替代原来 handleDiagnosisTest / handleGameTest 里重复的转义链
      escapeAttr(str) {
        return String(str || '')
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      },

      // ★ 删除单条闯关记录（二次确认，删除后本地不再保存）
      handleDeleteGameRecord(recordId) {
        this.$confirm('删除后该闯关记录及本地缓存的报告将无法恢复，确定删除？', '提示', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(() => {
          if (deleteGameRecord(this.currentKgId, this.currentLevel, recordId)) {
            this.refreshGraphStats(); // 内部已重新 getGameRecords，列表即时更新
            this.$message.success('已删除');
          } else {
            this.$message.warning('记录不存在或已删除');
          }
        }).catch(() => {});
      },

      // ★ 一键清空全部闯关记录
      handleClearGameRecords() {
        const count = this.gameRecords.length;
        this.$confirm(
          `将删除全部 ${count} 条闯关记录及本地缓存报告，无法恢复，确定删除？`,
          '提示',
          {
            confirmButtonText: '全部删除',
            cancelButtonText: '取消',
            type: 'warning',
          }
        ).then(() => {
          if (clearGameRecords(this.currentKgId, this.currentLevel)) {
            this.refreshGraphStats();
            this.$message.success('已清空全部闯关记录');
          } else {
            this.$message.warning('没有可删除的记录');
          }
        }).catch(() => {});
      },
      // 第 5 轮兜底：后端异常未给关键字，按失败收尾
      _finishGameAsFail(gs, aiContentArg, finalMsg, reason) {
        console.warn('[AdvancedLearning] 闯关兜底收尾:', reason);
        const lastNode = (gs.path || '').split('->').pop();
        if (lastNode) {
          markNodeGame(this.currentKgId, this.currentLevel, lastNode, false);
          if (!this._destroyed) this.$nextTick(() => this.refreshNodeGameMarks());
        }
        finishGameSession(
          this.currentKgId, this.currentLevel, false,
          this.extractReportSummary(finalMsg || reason),
          aiContentArg?.id || '',
          finalMsg || reason
        );
        this.gameStep = 0;
        this.isDiagnosing = false;
        this.isGameMode = false;
        this.refreshGraphStats();
        if (!this._destroyed) {
          this.$message.warning('本轮闯关失败，再接再厉！');
        }
      },
      // ===== ★ 闯关报告查看：只读 progressStore 本地缓存 =====
      viewGameReport(recordId) {
        const record = getGameRecords(this.currentKgId, this.currentLevel).find(r => r.id === recordId);
        if (!record) { this.$message.warning('未找到该闯关记录'); return; }
        if (record.report) {
          const header = `<p><strong>🎮 闯关分析报告（${record.result === 'success' ? '👑 闯关成功' : '闯关失败'}）</strong></p>`
            + `<p style="color:#999;font-size:12px;">范围：${record.path} ·${record.questions}轮 · `
            + `${this.formatDuration(record.duration)} ·${this.formatTime(record.time)}</p><hr/>`;
          this.viewingReport = true;
          this.setContent(header + md.render(record.report), { scrollTo: 'top' });
          this.showGameHistory = false;
          return;
        }
        this.setContent(`<p style="color:orange;">该报告未缓存到本地。</p>`, { scrollTo: 'top' });
      },
      // ★ 新弹窗恢复后台流现场：内存流优先，快照兜底（生产环境整页刷新后内存任务丢失）
      // ★ 返回值：true = 恢复到了流内容/提示（调用方不应再写占位文案），false = 没有任何流
      restoreBackgroundFlow() {
        if (!this.currentKgId) return false;
        const flowKey = getFlowKey(this.currentKgId, this.currentLevel);
        // ★ 内存流没有时读 sessionStorage 快照（getFlowOrSnapshot 由 backgroundFlow.js 导出）
        const t = getFlowOrSnapshot(flowKey);
        if (!t) return false;
        this._flowKey = flowKey;

        // ── 分支 A：快照流（内存任务已随页面刷新丢失，只有落盘的只读现场）──
        if (t.snapshotOnly) {
          this.loading = false;
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          this.nodeStreamActive = false;
          if (t.lastHtml) {
            // 上次已流出的内容完整还原
            this.setContent(t.lastHtml, { scrollTo: 'top' });
            return true; // ★ 有内容恢复
          }
          if (t.failed) {
            // 无内容但 know 失败原因 → 明确提示
            const tip = t.errText === 'BUSY' ? '上次请求排队中未完成'
              : t.errText === 'EMPTY' ? '上次未获取到内容'
              : `上次请求${t.errText || '异常'}，请重新发起`;
            this.setContent(`<p style="color:orange;">${tip}</p>`, { scrollTo: 'top' });
          } else if (t.busy || t.emptyResult) {
            const tip = t.busy ? '上次请求排队中未完成' : '上次未获取到内容';
            this.setContent(`<p style="color:orange;">${tip}</p>`, { scrollTo: 'top' });
          } else {
            // 只有元数据没有内容（极端情况）
            this.setContent('<p style="color:orange;">上次生成已中断，请重新点击节点或诊断测试</p>', { scrollTo: 'top' });
          }
          return true; // ★ 有提示也算恢复（不写占位文案）
        }

        // ── 分支 B：内存流仍在跑 / 已终态（正常重开弹窗，页面未刷新）──
        if (t.type === 'node') {
          this.loading = !t.done && !t.failed;
          this.nodeStreamActive = this.loading;
          this.isNormalChatActive = false;
        } else {
          this.isDiagnosing = !t.done && !t.failed;
          this.isNormalChatActive = !!(t.isNormalChat && this.isDiagnosing); // ★ 恢复对话标记
          this.nodeStreamActive = this.isDiagnosing;
        }
        // 显示后台已生成内容（done/failed 由 syncFlowView 接管终态）
        if (t.lastHtml) {
          this.setContent(t.lastHtml, { scrollTo: 'top' });
          return true; // ★ 有内容恢复
        }
        if (!t.done && !t.failed) {
          this.setContent('<p>⏳ 后台生成中，请稍候…</p>');
          return true; // ★ 生成中提示也算恢复
        }
        return false;
      },


        // ★ service 状态 → 视图（替代原来流内直接 setContent 的所有分支）
      syncFlowView() {
        const t = getFlow(this._flowKey);

        // ── 分支 0：本图谱当前没有活跃流 ──
        // （可能被 stopFlow 清掉了，或从未发起）
        if (!t) return;

        // ── 分支 1：正在查看诊断报告 → 一律不打断 ──
        // service 的 lastHtml 天然就是缓冲区，backToGenerating 时直接读它
        if (this.viewingReport) return;

        // ── 分支 2：失败态 ──
        if (t.failed) {
          if (t.errText === 'BUSY') {
            // 后端忙碌占位
            this.setContent('<p style="color:orange;">请求排队中，请稍候重试</p>');
          } else if (t.errText === 'EMPTY') {
            // 完成但无内容
            this.setContent('<p style="color:orange;">未获取到内容，请稍后重试</p>');
          } else {
            // 真实异常：正文（如有）+ 错误提示
            const errHtml = (t.lastHtml || '') +
              `<p style="color:red;">请求${t.errText}，请重试</p>`;
            this.setContent(errHtml, { scrollTo: 'top' });
          }
          // 终态：复位两种流的状态
          this.loading = false;
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          this.nodeStreamActive = false;
          this.chatWithLoading = false;
          return;
        }

        // ── 分支 3：节点流正常完成 ──
        if (t.done && t.type === 'node') {
          if (t.busy) {
            this.setContent('<p style="color:orange;">请求排队中，请稍候重试</p>');
          } else if (t.emptyResult) {
            this.setContent('<p style="color:orange;">未获取到内容，请检查节点或稍后重试</p>');
          } else if (t.lastHtml) {
            this.setContent(t.lastHtml, { scrollTo: 'top' });
          }
          this.loading = false;
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          this.nodeStreamActive = false;
          this.appendDownloadButtons();
          return;
        }

        // ── 分支 4：诊断流正常完成 ──
        if (t.done && t.type === 'diag') {
          if (t.lastHtml) this.setContent(t.lastHtml, { scrollTo: 'top' });
          this.loading = false;
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          this.nodeStreamActive = false;
          const s = getDiagSession(this.currentKgId, this.currentLevel);

          if (s && s.step >= this.DIAG_TOTAL) this.showDiagnosisHistory = true;  // 仅最终报告轮
          this.appendDownloadButtons();
          return;
        }

        // ── 分支 5：进行中 → 流式渲染 ──
        if (t.lastHtml) {
          // 不传 scrollTo：保持当前滚动位置，内容流式追加不跳动
          this.setContent(t.lastHtml);
        }
        // 无 lastHtml（刚开始，msg 还是 'loading'）：
        // 保持 onNodeClick/handleDiagTextClick 里设置的「⏳ 加载中…」不动
      },
      // ★ 新增：带描述信息的路径（仅用于提交后端的 payload）
      buildDiagPayloadPath(d) {
        const parts = [];
        let node = d;
        while (node) {
          if (node.data && node.data.name) {
            let part = node.data.name;

            // graphRender.js 确认描述字段为 d.data.description
            const desc = node.data.description;
            if (desc && typeof desc === 'string' && desc.trim()) {
              // 截断过长描述 + 清理破坏 HTML 属性的字符
              const cleanDesc = desc.trim()
                .replace(/["<>]/g, '')
                .substring(0, 50);
              const displayDesc = cleanDesc.length < desc.trim().length
                ? cleanDesc + '…'
                : cleanDesc;
              part += `（${displayDesc}）`;
            }

            parts.unshift(part);
          }
          node = node.parent;
        }
        return parts.join('->');
      },

      // ★ 共享图谱「使用图谱」回调：已收藏成功，返回本页并直接加载
      async handleUseSharedGraph({ kgid, level, name }) {
        this.showSharedDialog = false;
        // 刷新右侧「我的图谱」列表 → 收藏结果立即可见
        await this.fetchUserGraphs();
        const g = this.userGraphs.find(x => x.kgid === kgid && x.level === level);
        if (g) {
          if (name) g.name = name;
          await this.loadGraph(g); // 复用现有加载逻辑，锁定 level、恢复诊断、刷新统计
          this.setContent('<p>共享图谱已就绪，点击节点开始学习</p>', { scrollTo: 'top' });
        } else {
          this.$message.info('图谱已收藏，可在右侧「我的图谱」中点击加载');
        }
      },

      // ★ 判断图谱是否置顶
      isPinned(kgid) {
        return this.pinnedKgIds.includes(kgid);
      },

      // ★ 置顶/取消置顶
      async togglePin(kgid) {
        const idx = this.pinnedKgIds.indexOf(kgid);
        if (idx > -1) {
          // 取消置顶
          this.pinnedKgIds.splice(idx, 1);
          this.$message.info('已取消置顶');
        } else {
          // 置顶
          this.pinnedKgIds.unshift(kgid);
          this.$message.success('已置顶');
        }
        // 持久化到 localStorage
        localStorage.setItem('kg_pinned_list', JSON.stringify(this.pinnedKgIds));
        this.activeMenuKgId = null; // 关闭菜单
        // 强制刷新列表顺序
        this.userGraphs = [...this.userGraphs];
      },

      // ★ 删除图谱：取消收藏（必须传图谱自带 level，后端硬性校验）+ 清本地 + 移出列表
      async handleDeleteGraph(g) {
        const { kgid, level } = g;
        try {
          await this.$confirm(
            '删除后将同时取消收藏，并清空该图谱的学习进度与诊断记录，无法恢复。确定删除？',
            '删除确认',
            { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
          );

          // 1. 取消收藏：level 必传且必须为图谱卡片自带值
          //    后端逻辑：按 level 分别去 kgs(h)/mkgs(m)/pkgs(p) 中删除；
          //    level 缺失直接 400"非法访问"，传错学段则 202"不存在该图谱"
          const res = await removeGraph({ kgid, level });
          const status = res?.status ?? res?.code ?? 0;

          if (status === 400 || status >= 500) {
            // 400: level 缺失（理论上不会发生，防御）；5xx: 服务端异常
            this.$message.error('删除失败：' + (res?.data?.message || '后端取消收藏失败'));
            return;
          }

          if (status === 202) {
            // 202 = "后端该学段列表中已无此图谱"
            // 幂等处理：可能之前删过后端成功但本地未清理，或本就未收藏。
            // 继续执行本地清理，保证前后端最终一致
            console.warn('[AdvancedLearning] 后端无此收藏(202)，按幂等删除继续清理本地:', kgid, level);
          }
          // 200：正常成功，继续

          // 2. 清空该图谱所有本地记录（学习进度 + 诊断记录）
          removeGraphProgress(kgid, level);
          removeCachedGraph(kgid, level);
          // 3. 同步清理置顶列表
          const pinIdx = this.pinnedKgIds.indexOf(kgid);
          if (pinIdx > -1) {
            this.pinnedKgIds.splice(pinIdx, 1);
            localStorage.setItem('kg_pinned_list', JSON.stringify(this.pinnedKgIds));
          }

          // 4. 若删除的是当前打开的图谱 → 重置界面（splice 前做）
          if (this.currentKgId === kgid && this.currentLevel === level) {
            this.resetViewMode();
            this.setContent('<p>点击「知识地图节点」学习</p>');
            this.nodePath = '';
            this.currentKgId = '';
            this.currentPaintData = null;
            this.diagStep = 0;
            this.isDiagnosing = false;
            this.isGameMode = false;
            this.graphStats = { learned: 0, mastered: 0, total: 0, percent: 0, diagnosisRecords: [] };
            if (this.$refs.modalGraph) this.$refs.modalGraph.innerHTML = '';
          }

          // 5. 移出列表 + 以服务端为准重新拉取
          const idx = this.userGraphs.findIndex(x => x.kgid === kgid && x.level === level);
          if (idx > -1) this.userGraphs.splice(idx, 1);
          await this.fetchUserGraphs();

          this.$message.success(status === 202 ? '该图谱已不在收藏中，本地记录已清理' : '删除成功');
          this.activeMenuKgId = null;
        } catch (e) {
          if (e === 'cancel' || e === 'close') return;
          console.error('[AdvancedLearning] 删除图谱失败:', e);
          this.$message.error('删除失败：' + (e.message || '未知错误'));
        }
      },

      async handleUseImportedGraph(payload) {
        const { kgid, level, name } = payload || {};
        if (!kgid) return;

        // 1. 刷新右侧「我的图谱」列表 → 导入的图谱立即出现
        await this.fetchUserGraphs();

        // 2. 在列表中找到它，走与点击卡片完全一致的 loadGraph
        const g = this.userGraphs.find(x => x.kgid === kgid && x.level === level);
        if (g) {
          if (name) g.name = name;
          await this.loadGraph(g);   // ★ 内部已处理 currentLevel/currentKgId/诊断会话恢复/统计刷新
        } else {
          // 列表刷新后仍未找到（后端查询有延迟等），提示用户稍后在列表点击
          this.$message.info('图谱已保存，可在右侧「我的图谱」中点击加载');
        }
        this.setContent('<p>导入图谱已就绪，点击节点开始学习</p>', { scrollTo: 'top' });
      },

      // ★ 导出为 zip：内部含 progress.json（全部元数据）
      async handleExportZip() {
        if (this.exporting) return;
        this.exporting = true;
        try {
          const zip = new JSZip();
          const json = exportAllProgress();
          zip.file('progress.json', json);
          zip.file('README.txt',
            `知识图谱学习数据备份\n导出时间：${new Date().toLocaleString()}\n` +
            `导入方式：本弹窗「导入数据」选择此 zip 文件即可恢复全部状态。\n`);
          const blob = await zip.generateAsync({ type: 'blob' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `kg-progress-backup-${new Date().toISOString().slice(0, 10)}.zip`;
          a.click();
          URL.revokeObjectURL(url);
          this.$message.success('导出成功');
        } catch (e) {
          console.error('[AdvancedLearning] 导出失败:', e);
          this.$message.error('导出失败：' + (e.message || '未知错误'));
        } finally {
          this.exporting = false;
        }
      },

      // ★ 导入 zip：解压 progress.json，二次确认后恢复
      async handleImportZip(e) {
        const file = e.target.files?.[0];
        e.target.value = ''; // 允许重复选同一文件
        if (!file) return;
        try {
          const zip = await JSZip.loadAsync(file);
          const entry = zip.file('progress.json') || Object.values(zip.files).find(f => f.name.endsWith('.json'));
          if (!entry) { this.$message.error('zip 中未找到 progress.json'); return; }
          const text = await entry.async('string');

          const { data } = JSON.parse(text).graphs || {};
          const cnt = Object.keys(JSON.parse(text).graphs || {}).length;
          this.$confirm(
            `备份中包含 ${cnt} 个图谱的学习记录与诊断报告。\n` +
            `点击「合并」保留现有记录并合并，点击「覆盖」清空现有数据后完全恢复备份。`,
            '导入确认', {
              distinguishCancelAndClose: true,
              confirmButtonText: '合并导入',
              cancelButtonText: '覆盖导入',
              type: 'warning',
            }
          ).then(() => this.doImport(text, 'merge'))
          .catch(action => { if (action === 'cancel') this.doImport(text, 'replace'); });
        } catch (err) {
          console.error('[AdvancedLearning] 导入失败:', err);
          this.$message.error('导入失败：文件损坏或格式不正确');
        }
      },

      doImport(jsonText, mode) {
        const res = importAllProgress(jsonText, mode);
        if (!res.ok) { this.$message.error(res.msg); return; }
        // 导入后刷新当前弹窗的一切状态
        this.userGraphs = [];
        this.fetchUserGraphs().then(() => {
          // 若当前图谱在列表里，重新加载以恢复其数据视图
          const cur = this.userGraphs.find(g => g.kgid === this.currentKgId && g.level === this.currentLevel);
          if (cur) this.loadGraph(cur);
          else this.refreshGraphStats();
        });
        this.$message.success(`导入成功（${res.count} 个图谱已${mode === 'replace' ? '覆盖' : '合并'}）`);
      },
    // ===== ★ 节点状态标记（供 mixin 绘制钩子调用）=====
    // d 为 d3 节点，返回 'learned' | 'mastered' | 'not'
    getNodeStatusForNode(d) {
      const name = d?.data?.name;
      if (!name || !this.currentKgId) return 'not';
      return getNodeStatus(this.currentKgId, this.currentLevel, name);
    },

    // ★ 删除单条诊断记录（二次确认，删除后本地不再保存）
    handleDeleteRecord(recordId) {
      this.$confirm('删除后该诊断记录及本地缓存的报告将无法恢复，确定删除？', '提示', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        if (deleteDiagnosisRecord(this.currentKgId, this.currentLevel, recordId)) {
          this.resetViewMode(); // 若正查看该报告，退出查看模式
          this.refreshGraphStats();
          this.$message.success('已删除');
        } else {
          this.$message.warning('记录不存在或已删除');
        }
      }).catch(() => {});
    },

    // ★ 一键清空全部诊断记录
    handleClearAllRecords() {
      const count = this.graphStats.diagnosisRecords.length;
      this.$confirm(`将删除全部 ${count} 条诊断记录及本地缓存报告，无法恢复，确定删除？`, '提示', {
        confirmButtonText: '全部删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        if (clearDiagnosisRecords(this.currentKgId, this.currentLevel)) {
          this.resetViewMode();
          this.refreshGraphStats();
          this.$message.success('已清空全部诊断记录');
        } else {
          this.$message.warning('没有可删除的记录');
        }
      }).catch(() => {});
    },

    getGraphContainer() {
      return this.$refs.modalGraph;
    },

    handleBack() {
      this.$emit('close');
    },

    // ===== ★ level 工具（只读，不写 currentLevel）=====

    // 学段中文标签
    levelLabel(level) {
      return { h: '高中', m: '初中', p: '小学' }[level] || level || '';
    },

    // ★ 校验 (kgid, level) 组合是否存在于图谱列表中
    isGraphInList(kgid, level) {
      if (!kgid || !level) return false;
      return this.userGraphs.some(g => g.kgid === kgid && g.level === level);
    },

    // ★ 校验当前 level 是否可用（只判断，绝不修改 currentLevel）
    //    两级放宽，绝不错杀正确的 level：
    //    1) level 能映射到智能体 → 基本可用，放行
    //    2) 列表已加载且 (kgid, level) 配对存在 → 加分项
    //       列表未加载 / 配对不在列表（初始传入图谱可能不属于"我的图谱"）→ 不拦截
    ensureLevelResolved() {
      if (!LEVEL_ROBOT_KEY[this.currentLevel]) {
        console.warn('[AdvancedLearning] level 无法映射到智能体:', this.currentLevel,
          'kgid =', this.currentKgId);
        return false;
      }
      // 列表已加载时做一次提示性校验（不拦截，只留痕）
      if (this.userGraphs.length
          && !this.isGraphInList(this.currentKgId, this.currentLevel)) {
        console.warn('[AdvancedLearning] (kgid, level) 不在图谱列表中（初始传入图谱，放行）:',
          this.currentKgId, this.currentLevel);
      }
      return true;
    },

    // ===== ★ level → 智能体（只读 store.robots，不写任何主窗口状态）=====

    // ★ 按当前 level 取对应智能体对象
    getRobotByLevel(level) {
      const key = LEVEL_ROBOT_KEY[level];
      if (!key) return null;
      return this.$store.state.robots?.[key] || null;
    },

    // ★ 按当前 level 取 robotId：取不到返回空，由调用方显式提示
    //    （不回退 store.robotId——那可能是另一个学段的智能体，导致请求走错）
    getRobotIdByLevel(level) {
      const robot = this.getRobotByLevel(level);
      const id = robot?.robotId || robot?.id || '';
      if (!id) {
        console.warn('[AdvancedLearning] level=%s 对应智能体 robots.%s 缺少 robotId',
          level, LEVEL_ROBOT_KEY[level]);
      }
      return id;
    },

    // ★ 按当前 level 取头像
    getRobotAvatarByLevel(level) {
      return this.getRobotByLevel(level)?.avatar || '';
    },

    // ★★★ 获取某学段智能体的图谱专用会话（最终版）★★★
    // purpose: 'node' 节点内容流 / 'diag' 诊断流 —— 各自独立会话，互不污染
    // ★ 持久化到 sessionStorage：弹窗重开/页面刷新后复用同一 sessionId，
    //   后端按 sessionId 调历史会话 → 诊断上下文跨弹窗延续（核心需求）。
    //   关闭整个浏览器标签页后自然过期，符合"页面级临时会话"语义。
    async getSessionIdForLevel(level, purpose = 'node') {
      const key = LEVEL_ROBOT_KEY[level];
      if (!key) return '';

      // 1. 弹窗内存缓存（按 purpose 隔离）
      const memKey = `${purpose}_${key}`;
      if (this.sessionMap[memKey]) return this.sessionMap[memKey];

      // 2. 本页面会话内已建过 → 从 sessionStorage 恢复（弹窗重开不丢记忆）
      const ssKey = `kg_session_${purpose}_${key}`;
      const cached = sessionStorage.getItem(ssKey);
      if (cached) {
        this.sessionMap[memKey] = cached;
        console.log('[AdvancedLearning] 复用持久化会话:', ssKey, cached);
        return cached;
      }

      // 3. 新建会话
      const robots = this.$store.state.robots || {};
      const robot = robots[key];
      if (!robot || typeof robot.startNewSession !== 'function') {
        console.warn('[AdvancedLearning] 智能体 %s 不存在或缺少 startNewSession，实际 keys =', key, Object.keys(robots));
        return '';
      }

      try {
        const res = await robot.startNewSession();
        const sid = res?.data?.sessionId || res?.sessionId || res?.data?.id || '';
        if (!sid) {
          console.warn('[AdvancedLearning] 建会话返回无 sessionId:', res);
          return '';
        }
        // 4. 双写：组件内存 + sessionStorage（下次打开弹窗直接复用）
        this.sessionMap[memKey] = sid;
        sessionStorage.setItem(ssKey, sid);
        console.log('[AdvancedLearning] 已创建图谱专用会话:', ssKey, sid);
        return sid;
      } catch (e) {
        console.warn('[AdvancedLearning] 创建图谱会话失败:', key, e);
        return '';
      }
    },

    // ★ 离开报告页/清理缓冲的统一复位
    resetViewMode() {
      this.viewingReport = false;
      this.pendingStreamHtml = '';
      this.pendingDone = false;
      this.nodeStreamActive = false;
      this.isNormalChatActive = false;
      this._pendingFlowDone = null;
    },

    // ★ 终止当前生成请求（节点内容流或诊断流）—— 增加二次确认
    stopGenerating() {
        if (!this._flowKey) return;
        const t = getFlow(this._flowKey);
        if (!t) return;

        const typeLabel = this.isGameMode ? '闯关'
            : (this.isDiagnosing ? '诊断'
            : (this.isNormalChatActive || this.chatWithLoading ? '对话' : '生成'));

        this.$confirm(
            `确定终止本次${typeLabel}吗？已生成的部分内容将保留，未完成部分将被中断。`,
            '终止确认',
            { confirmButtonText: '终止', cancelButtonText: '继续' + typeLabel, type: 'warning' }
        ).then(() => {
            // ★★★ 第一步：在复位前先快照当前状态（提示语要用）★★★
            const wasGame = this.isGameMode;
            const wasDiag = this.isDiagnosing && !this.isGameMode;
            const wasChat = this.isNormalChatActive || this.chatWithLoading;

            // ★ wasGame 的取值时机说明：
            // 原代码 `const wasGame = this.isGameMode` 本来就在复位前，是对的；
            // 真正要注意的是：如果以后有人把这两行往下挪到 `this.isGameMode = false` 之后，
            // wasGame 永远是 false，闯关终止的提示就错了。所以快照必须整体放在复位代码块最上面。

            // ── 原终止逻辑整体搬入，一字未改 ──
            stopFlow(this._flowKey, '用户终止');

            // ★★★ 第二步：复位，新增 isNormalChatActive ★★★
            this.loading = false;
            this.isDiagnosing = false;
            this.isGameMode = false;
            this.isNormalChatActive = false;   // ★ 新增
            this.nodeStreamActive = false;
            this.chatWithLoading = false;

            // 中断记录保留（持久化数据，与流无关）
            if (this.currentKgId && getDiagSession(this.currentKgId, this.currentLevel)) {
                abortDiagSession(this.currentKgId, this.currentLevel, '用户终止');
                this.diagStep = 0;
                this.refreshGraphStats();
            }

            // ★ 闯关会话同样中断
            if (this.currentKgId && getGameSession(this.currentKgId, this.currentLevel)) {
                abortGameSession(this.currentKgId, this.currentLevel, '用户终止');
                this.gameStep = 0;
                this.$nextTick(() => this.refreshNodeGameMarks());
            }

            this.setContent('<p style="color:orange;">已终止生成</p>');

            // ★★★ 第三步：提示语用快照值，不读已复位的响应式状态 ★★★
            this.$message.info(
                wasGame ? '已终止闯关'
                : wasDiag ? '已终止诊断'
                : wasChat ? '已终止对话'
                : '已终止生成'
            );
        }).catch(() => {
            // 用户取消 → 不做任何事，流继续跑
        });
    },


    // ★ 重置知识地图视图：重新加载当前图谱（等价于重新点击进入）
    resetGraphView() {
      const g = this.userGraphs.find(
        x => x.kgid === this.currentKgId && x.level === this.currentLevel
      );
      if (g) {
        this.loadGraph(g); // 内部已处理 rebuild、复位查看模式、恢复诊断会话、刷新统计
      } else {
        // 当前图谱不在"我的图谱"列表中（如初始从外部传入的图谱）→ 直接本地重建
        this.rebuildGraph();
        this.$nextTick(() => this.refreshNodeStatusMarks());
      }
    },
    setLayoutMode(mode) {
      if (typeof this.toggleGraphLayout !== 'function') {
        console.warn('[AdvancedLearning] mixin 中未找到 toggleGraphLayout 方法');
        return;
      }
      const wantTree = (mode === 'tree');
      if (wantTree !== this.isTreeMode) {
        this.toggleGraphLayout();
      }
    },

    buildNodePath(d) {
      const names = [];
      let node = d;
      while (node) {
        if (node.data && node.data.name) {
          names.unshift(node.data.name);
        }
        node = node.parent;
      }
      return names.join('->');
    },

    // ===== ★ 学习进度统计 =====

    collectNodeNames() {
      const names = [];
      const root = this.root || this.rootNode || (this.$svg && this.$svg.datum && this.$svg.datum());
      if (root && typeof root.each === 'function') {
        root.each(n => names.push(n.data.name));
      } else if (root && root.descendants) {
        root.descendants().forEach(n => names.push(n.data.name));
      } else {
        const walk = (node) => {
          if (!node) return;
          if (node.name) names.push(node.name);
          (node.children || []).forEach(walk);
        };
        walk(this.currentPaintData);
      }
      this.graphNodeNames = [...new Set(names)];

      if (this.graphNodeNames.length) {
        setGraphNodeTotal(this.currentKgId, this.currentLevel, this.graphNodeNames.length);
      }

      this.refreshGraphStats();
    },

    refreshGraphStats() {
      this.graphStats = getGraphStats(this.currentKgId, this.currentLevel, this.graphNodeNames);
      this.gameRecords = getGameRecords(this.currentKgId, this.currentLevel); // ★ 闯关记录随统计刷新

    },

    // 右侧卡片统计：用卡片自己的 g.level（注意必须按学段隔离，不能用 currentLevel）
    getCardStats(g) { return getGraphStats(g.kgid, g.level, []); },
    getCardPercent(g) { return this.getCardStats(g).percent; },
    getCardLearned(g) { const s = this.getCardStats(g); return s.learned + s.mastered; },
    getCardTotal(g) { return this.getCardStats(g).total; },
    getCardDiagCount(g) { return this.getCardStats(g).diagnosisRecords.length; },
    getCardActiveStep(g) { const s = this.getCardStats(g); return s.hasActiveSession ? s.activeSession.step : 0; },

    formatTime(t) {
      return new Date(t).toLocaleString();
    },
    formatDuration(ms) {
      if (!ms) return '';
      const m = Math.floor(ms / 60000);
      const s = Math.round((ms % 60000) / 1000);
      return m ? `${m}分${s}秒` : `${s}秒`;
    },

    // ===== ★ 统一内容更新入口：保护滚动位置 =====
    setContent(html, opts = {}) {
      this.contentRendered = html;
      this.$nextTick(() => this.syncOwnTexts()); 
      const box = this.$refs.contentSection?.querySelector('.content-box');

      if (opts.scrollTo === 'top') {
        this.$nextTick(() => {
          if (box) box.scrollTop = 0;
        });
      } else if (opts.scrollTo === 'view') {
        // 双重 nextTick 确保 DOM 完全更新后再滚动
        this.$nextTick(() => {
          this.$nextTick(() => {
            if (box) {
              // 直接滚动到底部，新交互元素在末尾，确保可见
              box.scrollTop = box.scrollHeight;
            }
          });
        });
      }
      // 无 scrollTo 或默认情况不改变滚动位置
    },

    // ===== 节点点击（按学段取智能体 + 图谱专用会话）=====
    async onNodeClick(d) {
      if (this.loading) return;
      // ★ 离开报告页/上一轮缓冲
      this.chatWithMode = false;
      this.resetViewMode();
      this.isGameMode = false; // ★ 节点内容流不是闯关

      // ★ 移动端点击节点后，自动将「学习详情」区域滚动到可见
      if (this.isMobile) {
        this.$nextTick(() => {
          const el = this.$refs.contentSection;
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }

      const name = d.data.name;
      if (!this.currentKgId || !name) {
        this.setContent('<p style="color:red;">缺少必要参数（kgid 或节点名称）</p>');
        return;
      }
      this.nodePath = this.buildNodePath(d);
      // ★ 暂存带描述的路径，供诊断测试提交后端使用
      this._diagPayloadPath = this.buildDiagPayloadPath(d);

      // ★ 记录：节点已学习
      markNodeLearned(this.currentKgId, this.currentLevel, name);
      this.refreshGraphStats();
      this.refreshNodeStatusMarks();

      // ★★★ 校验 level 可用（只判断，不改写 level，绝不拦截有效值）
      if (!this.ensureLevelResolved()) {
        this.setContent('<p style="color:orange;">图谱学段信息加载中，请稍后重试</p>');
        return;
      }

      // ★★★ 图谱专用会话 + 对应学段 robotId（配对确定，与主窗口无关）
      const sessionId = await this.getSessionIdForLevel(this.currentLevel, 'node');
      const robotId = this.getRobotIdByLevel(this.currentLevel);
      if (!sessionId) {
        this.setContent('<p style="color:orange;">会话初始化失败，请关闭弹窗后重试</p>');
        return;
      }
      if (!robotId) {
        this.setContent(`<p style="color:orange;">未找到该学段（${this.levelLabel(this.currentLevel)}）对应的智能体，请稍后重试</p>`);
        return;
      }

      this.loading = true;
      this.nodeStreamActive = true;
      this.setContent('<p>⏳ 加载中…</p>');

      const avatar = this.getRobotAvatarByLevel(this.currentLevel);
      const aiContent = createAutoMsg(avatar, this.$delete);
      aiContent.type = "graphCollect";
      aiContent.sessionId = sessionId;
      aiContent.robotId = robotId;
      aiContent.msg = "loading";
      aiContent.params = {
        text: name,
        kgid: this.currentKgId,
        clickName: name,
        level: this.currentLevel,
        fileList: [],
        sessionId,
        robotId,
        answerUrl: "/api/kgNode2report",
      };

      // ★ 弹窗自管消息：不入库（主窗口不显示），报告找回走 progressStore 本地缓存
      if (!aiContent.id) aiContent.id = nanoid();

      // ★★★ controller 交给 service 持有，组件不存引用
      // （旧流覆盖由 startNodeFlow 内部的 stopFlow 完成，这里不再 abort）
      const controller = new AbortController();
      aiContent.controller = controller;

      // ★★★ 本流绑定到当前图谱的 flowKey
      const flowKey = getFlowKey(this.currentKgId, this.currentLevel);
      this._flowKey = flowKey;

      try {
        const data = handleChatParams({ aiContent });

        // ★★★ chunk 数据处理器（handleMathData 纯内存操作，不碰 store/视图）
        const dataHandler = (json, content) => {
          if (!json.questions) json.questions = [];
          if (!json.base64) json.base64 = [];
          if (!json.plotly) json.plotly = {};
          try {
            rawHandleMathData(json, content);
          } catch (e) {
            console.warn('[AdvancedLearning] 数据处理异常:', e);
          }
        };

        // ★★★ 本地直连：fetchLocalStream 不写主窗口 store（原 fetchAndProcessData
        //   的 done 分支和错误分支会把消息 dispatch 进主窗口会话 → 副本来源，已断开）
        fetchLocalStream(
          `${getBaseURL()}/kgNode2report`,
          data,
          dataHandler
        ).catch(err => {
          if (err?.name !== 'AbortError') {
            console.error('[AdvancedLearning] 请求失败:', err);
            updateFlow(flowKey, { failed: true, errText: err.message || '请稍后重试' });
            // updateFlow 内部 notify → syncFlowView 渲染错误态并复位 loading
          }
        });

        // ★ 轮询 + 流式渲染 + 终态判定全在 service
        startNodeFlow(flowKey, {
          aiContent,
          controller,
          onDone: (finalMsg) => {
            // 组件级钩子：仅组件在场时被调（beforeDestroy 已 detach）
            // 节点流正常完成暂无组件级动作，预留扩展位
          },
        });
      } catch (error) {
        console.error('[AdvancedLearning] 请求失败:', error);
        this.loading = false;
        this.nodeStreamActive = false;
        this.setContent(`<p style="color:red;">加载失败：${error.message || '请稍后重试'}</p>`);
      }
    },


    onNodeImageClick() {},

    // 与 handleDiagnosisTest 完全同构，仅交互文本变为「路径闯关」
    handleGameTest() {
      this.chatWithMode = false;
      if (!this.nodePath) {
        this.$message.warning('请先点击知识地图的节点，再进行闯关游戏');
        return;
      }
      if (this.isDiagnosing) return;
      this.resetViewMode();

      // ★ 双路径变量：
      //   purePath —— 纯节点路径（显示用、落库用，源头就是 buildNodePath 产物）
      //   descPath —— 带描述路径（仅提交后端 payload 用，不再用于反推纯路径）
      const purePath = this.nodePath;
      const descPath = this._diagPayloadPath || this.nodePath;
      const payload = `${descPath}闯关`;

      // ★ 页面显示：纯节点名路径（不带描述），不再从 payload 反推
      const interactiveHtml =
        `<p>闯关范围：${purePath}</p>` +
        `<p><span class="interactive-text" ` +
        `data-action="MathChat" ` +
        `data-payload="${this.escapeAttr(payload)}" ` +
        `data-pure-path="${this.escapeAttr(purePath)}" ` +
        `data-desc-path="${this.escapeAttr(descPath)}">开始闯关</span></p>`;

      this.setContent(
        this.contentRendered.replace(/<p>闯关范围[\s\S]*?<\/p>\s*<p><span class="interactive-text[\s\S]*?<\/span><\/p>/, '') +
        interactiveHtml,
        { scrollTo: 'view', selector: '.interactive-text' }
      );
    },

    // ===== 诊断测试：注入交互文本 =====
    handleDiagnosisTest() {
      this.chatWithMode = false;
      if (!this.nodePath) {
        this.$message.warning('请先点击知识地图的节点，再进行诊断测试');
        return;
      }
      if (this.isDiagnosing) return;
      this.resetViewMode();

      // ★ 双路径变量：purePath 显示/落库用，descPath 仅提交后端用
      const purePath = this.nodePath;
      const descPath = this._diagPayloadPath || this.nodePath;
      const payload = `${descPath}诊断`;

      const interactiveHtml =
        `<p>诊断范围：${purePath}</p>` +
        `<p><span class="interactive-text" ` +
        `data-action="MathChat" ` +
        `data-payload="${this.escapeAttr(payload)}" ` +
        `data-pure-path="${this.escapeAttr(purePath)}" ` +
        `data-desc-path="${this.escapeAttr(descPath)}">诊断测试</span></p>`;

      this.setContent(
        this.contentRendered.replace(
          /<p>诊断范围[\s\S]*?<\/p>\s*<p><span class="interactive-text[\s\S]*?<\/span><\/p>/,
          ''
        ) + interactiveHtml,
        { scrollTo: 'view', selector: '.interactive-text' }
      );
    },

    syncOwnTexts() {
      const box = this.$refs.contentSection?.querySelector('.content-box');
      if (!box) { this._ownTextPayloads && this._ownTextPayloads.clear(); return; }
      this._ownTextPayloads = new Set(
        [...box.querySelectorAll('.interactive-text')]
          .map(el => (el.getAttribute('data-payload') || '').trim())
          .filter(Boolean)
      );
    },

    // ===== 交互文本点击：分流「诊断测试」/「闯关游戏」/「普通对话」 =====
    handleDiagTextClick() {
      this._onDiagTextClick = async (e) => {
        const target = e.target.closest('.interactive-text');
        if (!target) return;
        // ★ 只处理本弹窗内容区内的交互文本
        if (!e.target.closest('.content-section')) return;
        // ★★★ 截停事件：主窗口监听器不再收到
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        const action = (target.dataset.action || target.getAttribute('data-action') || '').toLowerCase();
        if (action && action !== 'mathchat') return;

        let payload = (target.getAttribute('data-payload') || '').trim();
        if (!payload) return;

        // ════════ 分类 ════════
        const endsWithGame = payload.endsWith('闯关');
        const endsWithDiag = payload.endsWith('诊断');
        // 答题轮：payload 是选项文本（无后缀），必须是本组件当前内容区渲染过的文本
        // 且本地有对应进行中的会话，才算答题；否则一律按普通对话处理
        const hasDiagSession = !!getDiagSession(this.currentKgId, this.currentLevel);
        const hasGameSession = !!getGameSession(this.currentKgId, this.currentLevel);
        const inOwnTexts = this._ownTextPayloads && this._ownTextPayloads.has(payload);
        const isDiagAnswer = !endsWithGame && !endsWithDiag && inOwnTexts && hasDiagSession && !hasGameSession;
        const isGameAnswer = !endsWithGame && !endsWithDiag && inOwnTexts && hasGameSession && !hasDiagSession;
        const isGame = endsWithGame || isGameAnswer;
        const isDiagFlow = endsWithDiag || isDiagAnswer || isGame;
        // ★ 其他交互文本（含其他 MathChat）：不是诊断/闯关流程 → 走普通对话提交
        const isNormalChat = !isDiagFlow;

        // ★★★ 校验 level 可用
        if (!this.ensureLevelResolved()) {
          this.$message.warning('图谱学段信息加载中，请稍后重试');
          return;
        }

        // ★★★ 各自专用会话（purpose 隔离：'diag' / 'game' / 'node'）
        const sessionId = await this.getSessionIdForLevel(
          this.currentLevel,
          isGame ? 'game' : (isDiagFlow ? 'diag' : 'node')
        );
        if (!sessionId) {
          this.$message.warning('会话初始化失败，请稍后重试');
          return;
        }

        // ════════ 发起 vs 答题（仅诊断/闯关流程需要）════════
        if (isDiagFlow) {
          if (isGame) {
            if (endsWithGame) {
              // 发起闯关：作废旧闯关会话记录（本地），开新记录
              if (getGameSession(this.currentKgId, this.currentLevel)) {
                abortGameSession(this.currentKgId, this.currentLevel, '发起新闯关');
              }
              // ★ 互斥：发起闯关时作废残留的诊断会话
              if (getDiagSession(this.currentKgId, this.currentLevel)) {
                abortDiagSession(this.currentKgId, this.currentLevel, '切到闯关');
                this.diagStep = 0;
              }

              const purePath = (target.getAttribute('data-pure-path') || '').trim();
              if (!purePath) return;
              startGameSession(this.currentKgId, this.currentLevel, purePath);
              this.gameStep = 0;
              this.isDiagnosing = false;
              this.isGameMode = false;
            } else {
              // 闯关答题提交：记录一轮（★ 失效时静默返回，不再弹警告）
              const gs = recordGameAnswer(this.currentKgId, this.currentLevel);
              if (!gs) return;
              this.gameStep = gs.step;
              this.$message.info(`第${gs.step} 轮已提交`);
            }
          } else {
            // ── 诊断分支 ──
            if (endsWithDiag) {
              // 发起诊断：作废旧诊断会话记录（本地），开新记录
              if (getDiagSession(this.currentKgId, this.currentLevel)) {
                abortDiagSession(this.currentKgId, this.currentLevel, '发起新诊断');
              }
              // ★ 互斥：发起诊断时作废残留的闯关会话
              if (getGameSession(this.currentKgId, this.currentLevel)) {
                abortGameSession(this.currentKgId, this.currentLevel, '切到诊断');
                this.gameStep = 0;
              }

              const purePath = (target.getAttribute('data-pure-path') || '').trim();
              if (!purePath) return; // 防御：源头必带，缺失视为脏数据直接拒绝
              startDiagSession(this.currentKgId, this.currentLevel, purePath);
              this.diagStep = 0;
              this.isDiagnosing = false;
              this.isGameMode = false;
            } else {
              // 答题提交：记录一轮（★ 失效时静默返回，不再弹"诊断会话已失效"）
              const s = recordDiagAnswer(this.currentKgId, this.currentLevel, payload);
              if (!s) return;
              this.diagStep = s.step;
              this.$message.info(`第${s.step}/${this.DIAG_TOTAL} 题已提交`);
            }
          }

          this.refreshGraphStats();

          if (this.isDiagnosing) return;

          // ★ 2 秒内同 payload 重复提交直接拦截
          const now = Date.now();
          if (this._lastDiagRequest.payload === payload && now - this._lastDiagRequest.time < 2000) {
            console.warn('[AdvancedLearning] 重复提交已拦截:', payload);
            return;
          }
          this._lastDiagRequest = { payload, time: now };
        } else {
          // ── 普通对话：防重复即可，不做任何诊断/闯关记账 ──
          const now = Date.now();
          if (this._lastDiagRequest.payload === payload && now - this._lastDiagRequest.time < 2000) {
            console.warn('[AdvancedLearning] 重复提交已拦截:', payload);
            return;
          }
          this._lastDiagRequest = { payload, time: now };
        }

        const robotId = this.getRobotIdByLevel(this.currentLevel);
        if (!robotId) {
          this.$message.warning(`未找到该学段（${this.levelLabel(this.currentLevel)}）对应的智能体，请稍后重试`);
          return;
        }

        const avatar = this.getRobotAvatarByLevel(this.currentLevel);
        this.resetViewMode();
        this.isDiagnosing = isDiagFlow;
        this.isGameMode = isGame;
        this.isNormalChatActive = isNormalChat;

        this.setContent(isNormalChat ? '<p>⏳ 正在处理…</p>' : '<p>⏳ 正在提交…</p>');

        const aiContent = createAutoMsg(avatar, this.$delete);
        aiContent.retry = null; // ★ 本地消息不走重试（重试会走 ApiService → 主窗口入库路径）
        aiContent.remmandType = "";
        aiContent.sessionId = sessionId;
        aiContent.robotId = robotId;
        aiContent.msg = "loading";
        // ★ text 只发本轮 payload —— 历史由后端按 sessionId 维护
        aiContent.params = {
          text: payload,
          level: this.currentLevel,
          sessionId,
          robotId,
          fileList: [],
        };

        const controller = new AbortController();
        aiContent.controller = controller;
        if (!aiContent.id) aiContent.id = nanoid();

        const flowKey = getFlowKey(this.currentKgId, this.currentLevel);
        this._flowKey = flowKey;

        console.log(`[AdvancedLearning] ${isGame ? '闯关' : isDiagFlow ? '诊断' : '对话'}提交 sessionId =`, sessionId,
          'payload =', payload.slice(0, 40));

        try {
          // ★★★ 本地直连：fetchLocalStream 不写主窗口 store（消息只在组件内）
          fetchLocalStream(
            `${getBaseURL()}/MathChat`,
            handleChatParams({ aiContent }),
            (json, content) => {
              if (!json.questions) json.questions = [];
              if (!json.base64) json.base64 = [];
              if (!json.plotly) json.plotly = {};
              try {
                rawHandleMathData(json, content);
              } catch (e) {
                console.warn('[AdvancedLearning] 数据处理异常:', e);
              }
            }
          );
        } catch (error) {
          console.error('[AdvancedLearning] 请求失败:', error);
          this.setContent(`<p style="color:red;">请求失败：${error.message || '请稍后重试'}</p>`);
          this.isDiagnosing = false;
          this.isGameMode = false;
          this.isNormalChatActive = false;
          return;
        }

        startDiagFlow(flowKey, {
          aiContent,
          controller,
          payload,
          onDone: (finalMsg, aiContentArg, errText) => {
            // ══════════ 异常/终止分支 ══════════
            if (errText) {
              if (errText === 'BUSY' || errText === 'EMPTY') return;

              // ★ 普通对话：只复位状态，不碰诊断/闯关会话
              if (isNormalChat) {
                this.isDiagnosing = false;
                this.isGameMode = false;
                this.isNormalChatActive = false;
                return;
              }

              if (isGame) {
                if (getGameSession(this.currentKgId, this.currentLevel)) {
                  abortGameSession(this.currentKgId, this.currentLevel, errText);
                  this.gameStep = 0;
                  this.isDiagnosing = false;
                  this.isGameMode = false;
                  this.$nextTick(() => this.refreshNodeGameMarks());
                }
              } else {
                if (getDiagSession(this.currentKgId, this.currentLevel)) {
                  abortDiagSession(this.currentKgId, this.currentLevel, errText);
                  this.diagStep = 0;
                  this.isDiagnosing = false;
                  this.isGameMode = false;
                  this.refreshGraphStats();
                }
              }
              if (errText === '服务器异常') {
                const robotKey = LEVEL_ROBOT_KEY[this.currentLevel];
                const purpose = isGame ? 'game' : (isDiagFlow ? 'diag' : 'node');
                delete this.sessionMap[`${purpose}_${robotKey}`];
                sessionStorage.removeItem(`kg_session_${purpose}_${robotKey}`);
                console.warn(`[AdvancedLearning] ${purpose} 会话疑似失效，已清除待重建`);
              }
              return;
            }

            // ══════════ 正常完成分支 ══════════

            // ★ 普通对话：内容已由 service 流式渲染，只复位状态，不做任何记账
            if (isNormalChat) {
              this.isDiagnosing = false;
              this.isGameMode = false;
              this.isNormalChatActive = false;
              return;
            }

            // ── ★ 闯关收尾 ──
            if (isGame) {
              const gs = getGameSession(this.currentKgId, this.currentLevel);
              if (!gs) return;
              const step = gs.step;
              const success = finalMsg.includes('闯关成功');
              const failed = finalMsg.includes('闯关失败');

              if (!success && !failed) {
                if (step >= 5) {
                  this._finishGameAsFail(gs, aiContentArg, finalMsg, '后端第5轮未返回终局关键字，按失败兜底');
                }
                return;
              }

              const win = success && !failed;
              const lastNode = (gs.path || '').split('->').pop();
              if (lastNode) {
                markNodeGame(this.currentKgId, this.currentLevel, lastNode, win);
                if (!this._destroyed) this.$nextTick(() => this.refreshNodeGameMarks());
              }

              finishGameSession(
                this.currentKgId,
                this.currentLevel,
                win,
                this.extractReportSummary(finalMsg),
                aiContentArg?.id || '',
                finalMsg
              );
              this.gameStep = 0;
              this.isDiagnosing = false;
              this.isGameMode = false;
              this.refreshGraphStats();
              if (!this._destroyed) {
                if (win) {
                  this.gameWinCount = incrGameWinCount();
                  this.$message.success('🎉 闯关成功！');
                } else {
                  this.$message.warning('本轮闯关失败，再接再厉！');
                }
              }
              return;
            }

            // ── 诊断收尾 ──
            const session = getDiagSession(this.currentKgId, this.currentLevel);
            if (!session) return;
            if (session.step >= this.DIAG_TOTAL) {
              const record = finishDiagSession(
                this.currentKgId,
                this.currentLevel,
                this.extractReportSummary(finalMsg),
                aiContentArg?.id || '',
                finalMsg
              );
              const lastNode = (record?.path || session.path || '').split('->').pop();
              if (lastNode) {
                markNodeMastered(this.currentKgId, this.currentLevel, lastNode);
                if (!this._destroyed) this.refreshNodeStatusMarks();
              }
              this.diagStep = 0;
              this.isDiagnosing = false;
              this.isGameMode = false;
              this.refreshGraphStats();
              if (!this._destroyed) {
                this.$message.success('诊断完成，已生成分析报告');
                this.showDiagnosisHistory = true;
              }
            }
          },
        });
        // ★ 普通对话打标，恢复时区分终止按钮文案
        const task = getFlow(flowKey);
        if (task && isNormalChat) task.isNormalChat = true;   // 普通对话场景
      };
      document.addEventListener('click', this._onDiagTextClick, true);
    },



    // ★ 从报告页返回生成中/已完成的内容页
    backToGenerating() {
      this.viewingReport = false;
      const t = getFlow(this._flowKey);
      if (t && t.lastHtml) {
        this.setContent(t.lastHtml, { scrollTo: 'top' });
      } else {
        this.setContent('<p style="color:orange;">该轮请求已结束（未获取到有效内容）</p>', { scrollTo: 'top' });
      }
      this.pendingStreamHtml = '';
      this.pendingDone = false;
      this._pendingFlowDone = null;   // 收尾已在数据层即时完成，无需补做
    },

    // ★ 诊断流正常完成后的收尾
    // ★ 新增：诊断流正常完成时的统一入口
    maybeDiagFlowDone(finalMsg, aiContent) {
      const session = getDiagSession(this.currentKgId, this.currentLevel);
      if (!session) return;

      if (session.step >= this.DIAG_TOTAL) {
        // 组件在场 → 直接收尾；不在场（理论上 onDone 只在组件活着时被调）
        // 但组件可能刚关闭 → 检查 _destroyed 标记
        if (this._destroyed) {
          // 挂起，等重开补做
          updateFlow(getFlowKey(this.currentKgId, this.currentLevel),
            { pendingDiagDone: { finalMsg, aiContent } });
          return;
        }
      }
    },

    // 报告摘要提取
    extractReportSummary(mdText) {
      const plain = mdText
        .replace(/<[^>]+>/g, '')
        .replace(/[#*`>\s]+/g, ' ')
        .trim();
      return plain.slice(0, 100);
    },

    // 渲染报告到内容区（进入查看模式，滚到顶部）
    renderReport(record, reportText) {
      const header =
        `<p><strong>📋 诊断分析报告</strong></p>` +
        `<p style="color:#999;font-size:12px;">` +
        `范围：${record.path} · ${record.questions}题 · ` +
        `${this.formatDuration(record.duration)} · ${this.formatTime(record.time)}` +
        `</p><hr/>`;
      this.viewingReport = true;      // ★ 进入查看模式（流写入缓冲，不冲掉报告）
      this.setContent(header + md.render(reportText), { scrollTo: 'top' });
      this.showDiagnosisHistory = false;
    },

    // ===== ★ 报告查看：只读 progressStore 本地缓存 =====
    async viewDiagnosisReport(recordId) {
      const record = getDiagnosisRecord(this.currentKgId, this.currentLevel, recordId);
      if (!record) {
        this.$message.warning('未找到该诊断记录');
        return;
      }

      // ★ 本地缓存命中（诊断完成时已存全文）
      if (record.report) {
        this.renderReport(record, record.report);
        return;
      }

      // 无缓存 → 降级显示摘要
      this.setContent(
        `<p style="color:orange;">该报告未缓存到本地，无法查看。</p>` +
        (record.summary
          ? `<hr/><p><strong>摘要</strong></p><p>${record.summary}</p>`
          : ''),
        { scrollTo: 'top' }
      );
    },

    // ===== 侧栏 =====
    toggleLeft() {
      if (this.collapseLeft) {
        this.graphWidth = this.lastGraphWidth;
      } else {
        this.lastGraphWidth = this.graphWidth;
      }
      this.collapseLeft = !this.collapseLeft;
    },

    toggleRight() {
      if (this.collapseRight) {
        this.cardWidth = this.lastCardWidth;
      } else {
        this.lastCardWidth = this.cardWidth;
      }
      this.collapseRight = !this.collapseRight;
    },

    // ===== 我的图谱列表/加载 =====

    async fetchUserGraphs() {
      try {
        const { data } = await showGraphNames();
        if (!data || !data.kgnames) return;
        // ★ 返回格式：[[kgid, level], ...]（后端已确认 level 为 h/m/p）
        //    注意：这里只填列表，不触碰 currentLevel。
        //    currentLevel 只在初始 prop/robotId 反推 和 loadGraph(g.level) 两处写入。
        this.userGraphs = data.kgnames.map(([kgid, level]) => ({
          kgid,
          level: level || 'm',
          name: kgid,
        }));
      } catch (err) {
        console.error("获取用户图谱失败", err);
      }
    },

    async loadGraph(g) {
      // ★★★ 生成中禁止直接切换：终止并切换（一键）或取消 ★★★
      if (this.currentKgId) {
          const isSameGraph = g.kgid === this.currentKgId && g.level === this.currentLevel;
          if (!isSameGraph) {
              const curFlow = getFlow(getFlowKey(this.currentKgId, this.currentLevel));
              if (curFlow && !curFlow.done && !curFlow.failed) {
                  this.$confirm(
                      '当前图谱内容正在生成中，切换图谱将终止本轮生成（已生成部分保留）。是否终止并切换？',
                      '提示',
                      {
                          confirmButtonText: '终止并切换',
                          cancelButtonText: '继续生成',
                          type: 'warning',
                      }
                  ).then(() => {
                      // ── 用户确认：终止当前图谱的流 + 会话，然后放行切换 ──
                      // 注意：此处 this.currentKgId/currentLevel 仍是旧图谱坐标（尚未切换），
                      // 与被终止流一致，记账坐标天然正确——这正是本守卫的核心优点。

                      // 1. 终止流（写"切换图谱终止"原因，落盘快照保留已生成部分）
                      stopFlow(getFlowKey(this.currentKgId, this.currentLevel), '切换图谱终止');

                      // 2. 中断诊断/闯关会话（落"中断"记录，与手动终止语义一致）
                      if (getDiagSession(this.currentKgId, this.currentLevel)) {
                          abortDiagSession(this.currentKgId, this.currentLevel, '切换图谱终止');
                          this.diagStep = 0;
                      }
                      if (getGameSession(this.currentKgId, this.currentLevel)) {
                          abortGameSession(this.currentKgId, this.currentLevel, '切换图谱终止');
                          this.gameStep = 0;
                      }
                      this.refreshGraphStats();

                      // 3. 复位状态位（与 stopGenerating 的复位保持一致）
                      this.isDiagnosing = false;
                      this.isGameMode = false;
                      this.isNormalChatActive = false;
                      this.chatWithLoading = false;

                      // 4. 放行：流已终态，守卫不再拦截，递归调用完成切换
                      this.loadGraph(g);
                  }).catch(() => {
                      // 用户选「继续生成」→ 什么都不做，流继续跑
                  });
                  return;   // ★ 必须在这里 return，防止弹窗期间继续往下执行
              }
          }
      }
      // ★★★ 第一行就锁定 level（点击卡片条目自带的 g.level，与右侧标签同源，最可靠）
      // 接口成功与否都不影响它——宁可加载失败也不允许串学段
      if (!LEVEL_ROBOT_KEY[g.level]) {
        console.warn('[AdvancedLearning] 卡片 level 无效:', g.level, '卡片 =', g);
        this.$message.error(`图谱学段异常（level=${g.level}），请刷新后重试`);
        return;
      }
      this.currentLevel = g.level;
      
      // ★★★ 先查 IndexedDB 缓存（图谱数据永久不变，命中即用，不再请求后端）★★★
      const cached = await getCachedGraph(g.kgid, g.level);
      if (cached && cached.data && cached.data.kg) {
        console.log('[AdvancedLearning] 命中 IndexedDB 缓存:', g.kgid, g.level);
        this.applyGraphData(g, cached.data);
        return;
      }

      // ★ 缓存未命中 → 走后端，成功后写缓存（下次点击直接秒开）
      const data = await this.fetchAndCacheGraph(g);
      if (!data) return; // fetchAndCacheGraph 内部已提示错误
      this.applyGraphData(g, data);
    },

    // ★ 请求后端 + 写缓存。返回 data 或 null
    async fetchAndCacheGraph(g) {
      try {
        const { data } = await showGraph({ kgid: g.kgid, level: g.level });
        if (!data || !data.kg) {
          console.warn('未获取到图谱数据');
          this.$message.warning('未获取到图谱数据');
          return null;
        }
        // ★ 后台写缓存，不阻塞渲染（put 失败也不影响功能）
        setCachedGraph(g.kgid, g.level, data);
        return data;
      } catch (err) {
        console.error('图谱加载失败', err);
        this.$message.error('图谱加载失败，请稍后重试');
        return null;
      }
    },


    // ★ 抽出：原 loadGraph 中拿到 data 后的渲染/状态恢复逻辑（一字未改，只是搬家）
    applyGraphData(g, data) {
      this.currentKgId = data.kgid || g.kgid;
      console.log('[AdvancedLearning] 切换图谱: kgid =', this.currentKgId, 'g.level =', g.level);
      this.currentPaintData = data.kg;
      const realName = data.title || data.kg.name || g.kgid;
      g.name = realName;
      if (!this.currentPaintData.name) {
        this.$set(this.currentPaintData, 'name', realName);
      }
      this.rebuildGraph();
      this._flowKey = getFlowKey(this.currentKgId, this.currentLevel);
      this.resetViewMode();
      const restored = this.restoreBackgroundFlow();
      this.$nextTick(() => {
        this.refreshNodeStatusMarks();
        this.refreshNodeGameMarks();
      });
      if (!restored) {
        this.setContent('<p>点击「知识地图节点」学习</p>');
      }
      this.nodePath = '';
      const s = getDiagSession(this.currentKgId, this.currentLevel);
      this.diagStep = s ? s.step : 0;
      const gsn = getGameSession(this.currentKgId, this.currentLevel);
      this.gameStep = gsn ? gsn.step : 0;
      this.$nextTick(() => this.collectNodeNames());
      this.refreshGraphStats();
    },


    handleResize() {
      this.isMobile = window.innerWidth < 1023;
    },

    // ===== 拖拽 =====
    startDrag(type, e) {
      if (this.isMobile) return;
      e.preventDefault();
      if (type === 'left' && this.collapseLeft) this.toggleLeft();
      if (type === 'right' && this.collapseRight) this.toggleRight();
      this.isDragging = true;
      this.dragType = type;
      this.startX = e.clientX;
      this.startGraphWidth = this.graphWidth;
      this.startCardWidth = this.cardWidth;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },

    onDrag(e) {
      if (!this.isDragging) return;
      const deltaX = e.clientX - this.startX;
      const container = this.$refs.modalBody;
      if (!container) return;
      const containerWidth = container.getBoundingClientRect().width;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const minWidth = 10;
      const maxWidth = 85;

      if (this.dragType === 'left') {
        let newGraph = this.startGraphWidth + deltaPercent;
        this.graphWidth = Math.min(maxWidth, Math.max(minWidth, newGraph));
        if (!this.collapseLeft) this.lastGraphWidth = this.graphWidth;
      } else if (this.dragType === 'right') {
        let newCard = this.startCardWidth - deltaPercent;
        this.cardWidth = Math.min(maxWidth, Math.max(minWidth, newCard));
        if (!this.collapseRight) this.lastCardWidth = this.cardWidth;
      }
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .al-block-operation {
  margin-bottom: 5px;
  padding-top: 18px;
  border-top: 1px solid #c8c8c8;
  text-align: center;
  .al-dl-btn {
    margin-right: 10px;
    padding: 8px 24px;
    font-size: 12px;
    color: #fff;
    border: none;
    border-radius: 60px;
    cursor: pointer;
    background: linear-gradient(90deg, rgb(230, 140, 253) 0,  rgb(93, 103, 250) 100%);
    box-shadow: 0 4px 16px rgba(67, 106, 248, 0.4);
    transition: transform 0.2s, box-shadow 0.2s;
    &:hover { transform: translateY(-2px); }
    &:active { transform: translateY(0) scale(0.97); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:last-child { margin-right: 0; }
  }
}

// ★ 与我对话：底部聊天输入栏
.content-footer.chat-footer {
  position: relative;                    // @ 弹层锚点
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 10px));
  border-top: 1px solid #f0f0f0;
  background: #fff;

  // 已选 @kgid 标签区
  .cwt-kgid-area {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    .cwt-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 10px;
      border-radius: 12px;
      background: #ececff;
      color: #525dfc;
      font-size: 12px;
      .tag-del {
        cursor: pointer;
        font-style: normal;
        font-size: 11px;
        &:hover { color: #f56c6c; }
      }
    }
    .cwt-placeholder { color: #bbb; font-size: 12px; }
  }

  // 输入框 + 发送按钮
  .cwt-input-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    textarea {
      flex: 1;
      resize: none;
      box-sizing: border-box;
      border: 1px solid #dcdfe6;
      border-radius: 18px;
      padding: 8px 14px;
      font-size: 14px;
      outline: none;
      max-height: 100px;
      &:focus { border-color: #525dfc; }
    }
    .cwt-send {
      flex-shrink: 0;
      padding: 8px 20px;
      border: none;
      border-radius: 18px;
      color: #fff;
      cursor: pointer;
      font-size: 14px;
      background: linear-gradient(90deg, #525dfc, #de60fd);
      &:disabled { opacity: .5; cursor: not-allowed; }
    }
  }

  // 退出对话
  .cwt-exit {
    align-self: center;
    font-size: 14px;
    color:rgb(96, 2, 151);
    cursor: pointer;
    &:hover { color: #f56c6c; }
  }

  // @ 候选弹层：向上弹出，紧贴输入栏顶部
  .cwt-mention-pop {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 100%;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 10px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, .1);
    max-height: 180px;
    overflow-y: auto;
    z-index: 100;
    .mention-item {
      padding: 9px 14px;
      font-size: 13px;
      cursor: pointer;
      &:hover { background: #f0f4ff; color: #525dfc; }
      &.muted { color: #bbb; cursor: default; }
    }
  }
}

// ★ 底部按钮组新增的「与我对话」按钮
.content-footer .chat-btn {
  background: linear-gradient(90deg, #20b2aa, #2e8b57);
  margin-left: 12px;
}
// ★ 记录分组：标题行（含删除全部链接）+ 列表
.record-block {
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;

    .record-head {
        display: flex;
        align-items: center;
        padding: 8px 16px 0;
        .record-title {
            font-weight: 600;
            font-size: 13px;
            color: #333;
        }
        .diag-clear {
            margin-left: auto;
        }
    }
}
.game-btn { background: linear-gradient(90deg, #f7b733, #fc4a1a); margin-left: 12px; }
.crown-badge {
  margin-left: 14px; font-size: 16px; font-weight: 600; color: #f5a623;
  background: #fff8e6; border: 1px solid #f5d98a; border-radius: 16px;
  padding: 3px 12px; cursor: default; user-select: none; flex-shrink: 0;
}
.header-actions { display: flex; gap: 8px; margin-left: auto; }
.import-material-btn.io-btn {
  margin-left: 0;
  padding: 8px 14px;
  font-size: 13px;
  background:rgb(137, 91, 247);
  &:disabled { opacity: .6; cursor: not-allowed; }
}
// ★ 节点学习状态标记（黄=已学习，绿=已掌握）
::v-deep .graph-svg {
  .status-dot {
    fill: #e6a23c;              // 黄色：已学习
    stroke: #fff;
    stroke-width: 1.5;
    pointer-events: none;       // 不挡节点点击

    &.is-mastered {
      fill: #67c23a;            // 绿色：诊断完成已掌握
    }
  }
}
.import-material-btn {
  position: relative;
  margin-left: auto;
  display: flex; align-items: center; gap: 6px;
  padding: 8px 18px; border: none; border-radius: 22px;
  color: #fff; font-size: 14px; cursor: pointer;
  background: linear-gradient(90deg, rgb(82,93,252) 0, rgb(222,96,253) 100%);
  box-shadow: 0 4px 16px rgba(46,91,255,.3);
  transition: transform .2s, box-shadow .2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(46,91,255,.4); }
  &:active { transform: translateY(0) scale(.97); }
  .btn-icon { width: 15px; height: 15px; }
  // ★ 生成完成红点
  .ready-dot {
    position: absolute;
    top: 2px;
    right: 6px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #f56c6c;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, .8);
    animation: kg-dot-blink 1.2s ease-in-out infinite;
  }
}
.advanced-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 2000 !important; 
  display: flex;
  flex-direction: column;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
    // ★ 移动端让出顶部标题栏高度
  @media (max-width: 1023px) {
    top: 44px; // 改成你们标题栏的实际高度

    // 高度相应调整
    height: calc(100vh - 44px);
    @supports (height: 100dvh) {
      height: calc(100dvh - 44px);
    }
  }
  .modal-header {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background: #fff;
    flex-shrink: 0;
    // ★ 草稿纸入口图标（仅电脑端渲染）
    .rough-paper-entry {
      margin-left: 14px;
      font-size: 22px;
      cursor: pointer;
      color: #20b2aa;
      transition: all 0.2s ease;
      flex-shrink: 0;
      &:hover {
        color: #2e8b57;
        transform: scale(1.1);
      }
    }
    .header-left {
      display: flex;
      align-items: center;
      cursor: pointer;
      min-width: 0;

      .back-icon {
        width: 22px;
        height: 22px;
        color: rgb(118, 6, 245);
        margin-right: 12px;
        flex-shrink: 0;
        transition: opacity 0.2s;
        &:hover { opacity: 0.6; }
      }
      .modal-title {
        font-size: 18px;
        font-weight: 600;
        color: rgb(118, 6, 245);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .header-more { display: none; }
      // ★★★ 新增：移动端收起平铺按钮，显示更多菜单 ★★★
    @media (max-width: 1023px) {
      .header-actions { display: none !important; }

      .header-more {
        display: block;
        margin-left: auto;
        position: relative;

        .more-btn {
          padding: 8px 16px;
          font-size: 18px;
          line-height: 1;
        }

        .more-menu {
          position: absolute;
          right: 0;
          top: calc(100% + 6px);
          background: #fff;
          border: 1px solid #e4e7ed;
          border-radius: 10px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, .12);
          min-width: 150px;
          z-index: 2100;
          overflow: hidden;
          
          .menu-item {
            padding: 12px 16px;
            font-size: 14px;
            color: #333;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: background-color .15s, color .15s; // ★ 平滑过渡

            // ★★★ 悬停变色：鼠标放到哪一项，哪一项高亮 ★★★
            &:hover {
              background: #f0f4ff;   // 淡蓝背景（可换成 #f5f7ff 等）
              color: #525dfc;        // 文字同步变色，效果更明显
            }

            &:not(:last-child) { border-bottom: 1px solid #f0f0f0; }
            &:active { background: #f5f7ff; }
            &.disabled { color: #bbb; pointer-events: none; }
          }
        }
      }
    }
  }
  .modal-body {
    flex: 1;
    display: flex;
    flex-direction: row;
    padding: 0;
    gap: 0;
    overflow: hidden;
    min-height: 0;
    padding-bottom: env(safe-area-inset-bottom, 20px);

    // ==================== ★ 移动端布局 ====================
    &.isMobile {
      .advanced-modal .modal-body.isMobile .record-block .record-head {
          padding: 8px 12px 0;
      }
      display: flex !important;
      flex-direction: column !important;
      overflow-y: auto !important;   // 整个区域可滚动
      padding-bottom: calc(20px + env(safe-area-inset-bottom, 20px));
      .divider {
        display: none !important;
      }

      .graph-section,
      .content-section,
      .card-section {
        flex: 0 0 auto !important;   // 不伸缩，由内容决定高度
        width: 100% !important;
        border: none !important;
        border-bottom: 1px solid #eee;
        // 注意：这里不写 overflow: visible，否则会顶掉内部滚动
      }

      // ★★★ 强制排序 ★★★
      .card-section    { order: 0 !important; }  // 我的图谱 → 上
      .graph-section   { order: 1 !important; }  // 知识地图 → 中
      .content-section { order: 2 !important; }  // 学习详情 → 下

      // ----- 图谱区：固定高度以保证 SVG 正常显示 -----
      .graph-section {
        .modal-graph {
          height: auto;        
          max-height: 45vh; // 最大视口高度的一半，可调整
          min-height: 300px;
          overflow: hidden;    // SVG 本身渲染，不需要滚动
        }
      }

      // ----- 内容区：完全展开 -----
      .content-section {
        position: relative;
        z-index: 5;
        overflow: visible;    // ★ 新增：放开裁剪，@ 弹层才能向上弹出
        .content-box {
          overflow-y: auto;
          padding: 12px 16px;
          font-size: 15px;
          line-height: 1.7;
        }
        .content-footer {
          padding: 8px 0 calc(20px + env(safe-area-inset-bottom, 20px));
          .diagnosis-btn {
            padding: 10px 36px;
            font-size: 17px;
            border-radius: 28px;
          }
        }
        @media (max-width: 520px) {
          .content-footer {
            flex-wrap: nowrap;
            gap: 8px;
            padding-left: 8px;
            padding-right: 8px;

            .diagnosis-btn {
              flex: 1 1 0;
              min-width: 0;
              padding: 8px 0;
              font-size: 14px;
              white-space: nowrap;
            }
            .game-btn,
            .chat-btn {
              margin-left: 0;
            }
          }
        }
      }

      // ----- 卡片列表（我的图谱）：超过 4 条时内部滚动 -----
      .card-list {
        padding: 8px 12px;
        gap: 8px;
        overflow-y: auto;          // 默认允许滚动
        max-height: none;          // 默认不限高

        &.scrollable {             // 超过 4 条时由 class 激活限高
          max-height: 267px;       // 约 4.5 张卡片高度（露半张暗示可滚）
          -webkit-overflow-scrolling: touch;  // iOS 顺滑滚动
        }

        .graph-card {
          padding: 10px 12px;
          .card-name { font-size: 15px; }
          .level-tag { font-size: 12px; padding: 2px 10px; }
          .progress-bar { height: 8px; }
          .card-progress-text { font-size: 13px; }
        }
        .card-empty {
          font-size: 14px;
          line-height: 1.6;
          padding: 16px 0;
        }
      }

      // ----- 其他移动端样式微调 -----
      .section-title {
        padding: 8px 12px;
        font-size: 14px;
        .title-text { font-size: 14px; }
        .layout-icon { width: 28px; height: 28px; }
      }

      .progress-summary {
        flex-wrap: wrap;
        gap: 8px;
        font-size: 14px;
        padding: 8px 12px;
        .diag-entry { font-size: 14px; }
      }

      .diagnosis-history {
        padding: 6px 12px;
        .diag-item {
          font-size: 13px;
          flex-wrap: wrap;
          gap: 4px;
          padding: 4px 0;
          .diag-time { font-size: 12px; }
          .diag-path { flex-basis: 100%; }
          .diag-del { width: 20px; height: 20px; }
        }
      }

      .stop-generating {
        margin: 8px 12px 0;
        padding: 8px 16px;
        font-size: 15px;
        width: auto;
        justify-content: center;
        svg { width: 18px; height: 18px; }
      }

      .generating-banner {
        margin: 8px 12px 0;
        padding: 10px 16px;
        font-size: 14px;
        flex-wrap: wrap;
        .dot { width: 10px; height: 10px; }
      }
    }
  }

  .graph-section,
  .content-section,
  .card-section {
    border: 1px solid #eee;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    transition: flex-basis 0.25s ease;
  }

  .graph-section,
  .card-section { flex: 0 0 auto; }
  .content-section { flex: 1 1 0; min-width: 0; }

  .section-title {
    padding: 10px 14px;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    .title-text {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .layout-icon {
      width: 22px;
      height: 22px;
      margin-right: 6px;
      flex-shrink: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      svg { width: 100%; height: 100%; }
      &:hover, &.active { color: #409eff; }
    }
    .collapse-icon {
      margin-left: 0;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      svg {
        width: 100%;
        height: 100%;
        transition: transform 0.25s;
      }
      svg.collapsed { transform: rotate(180deg); }
      rect, line { transition: stroke 0.2s; }
      &:hover {
        rect, line { stroke: #409eff; }
      }
    }
  }

  .info-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: 4px;
  cursor: help;           // 悬停显示问号光标，暗示有提示
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b0b6c3;         // 淡灰，不抢眼
  transition: color 0.2s;

  &:hover {
    color: #409eff;       // 悬停变主题蓝
    }
  }

  .divider {
    width: 6px;
    background: #e0e0e0;
    cursor: col-resize;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover { background: #409eff; }
  }

  .modal-graph {
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }
  .diag-clear {
    color:rgb(243, 137, 137);
    font-size: 13px;
    cursor: pointer;
    padding: 6px 16px;
    &:hover { text-decoration: underline; }
  }
  // ★ 学习进度摘要 + 诊断历史
  .progress-summary {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 6px 16px;
    font-size: 13px;
    color: #666;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;

    .muted { color: #bbb; }
    .diag-step { color: #e6a23c; font-weight: 600; }
    .diag-entry {
      margin-left: auto;
      color: #409eff;
      cursor: pointer;
      &:hover { text-decoration: underline; }
    }
  }

  .diagnosis-history {
    max-height: 150px;
    overflow-y: auto;
    padding: 6px 16px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;

    .diag-item {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 12px;
      color: #666;
      padding: 3px 0;

      .diag-time { color: #999; flex-shrink: 0; }
      .diag-path { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .diag-meta { color: #999; flex-shrink: 0; }
      .ok { color: #67c23a; font-weight: 600; }
      .warn { color: #e6a23c; font-weight: 600; }
      .diag-view { color: #409eff; flex-shrink: 0; }
      .diag-del {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        cursor: pointer;
        color: #c0c4cc;
        display: flex;
        align-items: center;
        transition: color 0.2s;
        svg { width: 100%; height: 100%; fill: currentColor; }
        &:hover { color: #f56c6c; }
      }
      &.clickable {
        cursor: pointer;
        &:hover { background: #f0f4ff; }
      }
    }
  }

  // ★ 生成中：终止请求
  .stop-generating {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 10px 16px 0;
    padding: 6px 14px;
    background: #fdf1f0;
    border: 1px solid #f5c6c0;
    border-radius: 6px;
    font-size: 13px;
    color: #c45655;
    cursor: pointer;
    flex-shrink: 0;
    width: fit-content;
    transition: all 0.2s;

    &:hover {
      background: #fbe4e1;
      border-color: #e8948a;
    }
    svg { width: 14px; height: 14px; flex-shrink: 0; }
  }

  // ★ 查看报告时的返回横幅
  .generating-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px 16px 0;
    padding: 8px 14px;
    background: #f0f4ff;
    border: 1px solid #d6e0ff;
    border-radius: 6px;
    font-size: 13px;
    color: #3a5bd9;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s;

    &:hover { background: #e4ecff; }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #e6a23c;
      animation: blink 1.2s infinite;
      flex-shrink: 0;
      &.done { background: #67c23a; animation: none; }
    }
    .arrow { margin-left: auto; font-weight: 600; }
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  .content-box {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    // ★★★ 统一限制图片高度，防止大图撑爆内容区 ★★★
    ::v-deep img {
      max-height: 280px;   // 最大高度，可按需调整
      max-width: 100%;     // 同时防止超宽溢出
      width: auto;
      height: auto;
      object-fit: contain; // 保持比例不变形
      display: block;
      margin: 8px auto;    // 居中显示（可选）
      border-radius: 6px;  // 可选：圆角
    }

    ::v-deep p { margin: 0 0 8px; }
    ::v-deep pre {
      overflow-x: auto;
      background: #f6f8fa;
      padding: 10px;
      border-radius: 6px;
    }
    ::v-deep .interactive-text {
      color: #3498db;
      cursor: pointer;
      border-bottom: 1px dashed currentColor;
      transition: all 0.2s;
    }
    ::v-deep .interactive-text:hover {
      color: #e74c3c;
      border-bottom-style: solid;
    }
      /* 代码块容器（借鉴 chatMain.vue） */
  ::v-deep .code-block {
    margin: 0 auto;
    width: 90%;
    box-sizing: border-box;
  }

  /* 复制行（语言标识 + 复制按钮） */
  ::v-deep .code-top {
    margin: 0;
    padding: 6px 12px;
    width: 100%;
    height: 36px;
    display: flex;
    justify-content: space-between;
    border-radius: 10px 10px 0 0;
    background: var(--hover_icon_grey_2, rgba(79, 88, 102, 0.08)) !important;
    box-sizing: border-box !important;
    color: var(--txt-default-color);

    .language-type {
      font-size: 12px;
      font-weight: 600;
      line-height: 20px;
    }
    .copy-btn {
      padding: 2px 8px;
      font-size: 12px;
      border-radius: 6px;
      background: var(--hover_icon_grey_2, rgba(79, 88, 102, 0.08)) !important;
      cursor: pointer;
      &:hover { color: #409eff; }
    }
  }

  /* pre 与代码块同宽，避免横向溢出 */
  ::v-deep pre {
    box-sizing: border-box;
    margin: 0 0 8px !important;
    background: #f6f8fa;
    overflow: auto;
    width: 100%;
    code { width: 100%; }
  }
}

  .content-footer {
    flex-shrink: 0;
    padding: 10px 0 14px;
    display: flex;
    justify-content: center;
    border-top: 1px solid #f0f0f0;
    background: #fff;

    .diagnosis-btn {
      padding: 8px 32px;
      font-size: 15px;
      color: #fff;
      border: none;
      border-radius: 22px;
      cursor: pointer;
      background: linear-gradient(90deg, rgb(82, 93, 252) 0, rgb(222, 96, 253) 100%);
      box-shadow: 0 4px 16px rgba(46, 91, 255, 0.3);
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 28px rgba(46, 91, 255, 0.4);
      }
      &:active {
        transform: translateY(0) scale(0.97);
        box-shadow: 0 2px 8px rgba(46, 91, 255, 0.25);
      }
    }
  }

  .card-list {
    flex: 1;
    padding: 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .card-empty {
    color: #999;
    font-size: 13px;
    text-align: center;
    padding: 20px 0;
  }

  // ★ 图谱卡片：名称 + 学段标签 + 进度条 + 统计文本
  .graph-card {
    padding: 12px 14px;
    background: #f5f7ff;
    border: 1px solid #dbe4ff;
    cursor: pointer;
    transition: all 0.2s;
    word-break: break-all;
    // ★ 置顶标签
    .pin-tag {
      flex-shrink: 0;
      font-size: 11px;
      font-weight: 400;
      padding: 1px 6px;
      border-radius: 4px;
      color: #fff;
      background:rgb(165, 140, 109); 
    }

    &:hover { background: #ececff; }
    &.active {
      border-color: #7c6fd0;
      background: #ececff;
      color: #5b4aa8;
      font-weight: 600;
    }

    .card-name {
      font-weight: 600;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    // ★ 学段标签（h 高中 / m 初中 / p 小学）
    .level-tag {
      flex-shrink: 0;
      font-size: 11px;
      font-weight: 400;
      padding: 1px 6px;
      border-radius: 4px;
      color: #fff;
      background: #8a93b0;
      &.h { background:rgb(102, 92, 250); }  // 高中：红
      &.m { background: #409eff; }  // 初中：蓝
      &.p { background: #67c23a; }  // 小学：绿
    }

    .progress-bar {
      height: 6px;
      background: #e4e8f5;
      border-radius: 3px;
      overflow: hidden;
      margin-bottom: 5px;

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, rgb(82, 93, 252), rgb(222, 96, 253));
        border-radius: 3px;
        transition: width 0.4s ease;
      }
    }

    .card-progress-text {
      font-size: 12px;
      font-weight: 400;
      color: #8a93b0;
      .diag-step { color: #e6a23c; }
    }
    // ... 原有样式
    position: relative; // 新增：为三点图标定位
    
    // ★ 三点图标容器
    .more-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      cursor: pointer;
      color: #999;
      background: transparent;
      transition: all 0.2s;
      
      &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #666;
      }
      
      // 弹出菜单
      .dropdown-menu {
        position: absolute;
        top: 100%;
        right: 0;
        background: white;
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        min-width: 100px;
        z-index: 100;
        overflow: hidden;
        
        .menu-item {
          padding: 8px 16px;
          font-size: 13px;
          color: #333;
          cursor: pointer;
          transition: background-color 0.2s;
          
          &:hover {
            background: #f5f7fa;
          }
          
          &.danger {
            color: #f56c6c;
            &:hover {
              background: #fef0f0;
            }
          }
        }
      }
    }
  }

  // ★ 菜单动画
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}
</style>

<style lang="scss">
/* ★ 草稿纸弹窗：必须盖住 AdvancedLearning (z-index: 2000) */
.globalRoughPaperDialog {
  z-index: 3000 !important;

  .el-dialog {
    background: transparent !important;
    border: 2px solid #9dca67 !important;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(6, 156, 243, 0.3);
  }
  /* ★ header/body/footer 逐个透明（chatList 有此规则） */
  .el-dialog__header,
  .el-dialog__body,
  .el-dialog__footer {
    background: transparent !important;
  }
  /* ★ 画布面板透明，透出弹窗容器/底层页面 */
  .draw-panel {
    background: transparent !important;
  }
  /* ★ 工具栏黑色半透明背景（chatList 有此规则） */
  .draw-container .operate-group {
    background-color: rgba(0, 0, 0, 0.7);
  }

  .rough-paper-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .draft-icon { font-size: 20px; color: #9dca67; }
    span { color: rgb(4, 204, 226) !important; font-weight: 600; }
  }

  .el-dialog__headerbtn .el-dialog__close {
    color: rgb(250, 100, 62) !important;
    font-size: 24px;
    &:hover { color: rgb(247, 3, 15) !important; }
  }

  .dialog-footer button {
    background: rgba(0, 0, 0, 0.6) !important;
    border: 1px solid #9dca67 !important;
    color: #d4ff9d !important;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    &:hover { background: rgba(0, 0, 0, 0.8) !important; border-color: #ffd966 !important; color: #ffd966 !important; }
  }

  /* 全屏定位 */
  &.is-fullscreen .el-dialog {
    top: 0 !important;
    left: 0 !important;
    margin: 0 !important;
    transform: none !important;
    background: transparent !important;   /* ★ 补：全屏时背景透明 */
    border: none !important;
    box-shadow: none !important;         /* ★ 补：全屏时去阴影 */
    width: 100vw !important;
    height: 100vh !important;
    max-height: 100vh !important;
    border-radius: 0 !important;
  }
  &.is-fullscreen .el-dialog__header,
  &.is-fullscreen .el-dialog__footer {
    display: none !important;
  }
  &.is-fullscreen .el-dialog__body {
    padding: 0 !important;
    height: 100vh !important;
  }
  &.is-fullscreen .draw-panel {
    height: 100%;
    border-radius: 0;
  }
}
</style>

